import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import { Users, CreditCard, TrendingUp, Crown, Calendar, RefreshCw, Heart, Headphones, Trash2, Plus, Shield, ShieldCheck, UserPlus, LayoutDashboard, ClipboardList, Eye, Compass, Link2, Store, Image, Zap, Settings, SlidersHorizontal, Menu, X, Mail, MessageSquareQuote, Download } from 'lucide-react'
import { FLAG_DEFAULTS, FLAG_LABELS } from '../lib/featureFlags'
import TestimonialsAdmin from '../components/TestimonialsAdmin'

function ToggleBtn({ on, onClick }) {
  return (
    <button onClick={onClick} style={{ width:42, height:24, borderRadius:100, background:on?'#9BAF9B':'#D1C9C0', border:'none', cursor:'pointer', position:'relative', flexShrink:0, transition:'background 0.15s' }}>
      <span style={{ position:'absolute', top:3, left:on?21:3, width:18, height:18, borderRadius:'50%', background:'white', transition:'left 0.15s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }}/>
    </button>
  )
}

// Admin sections grouped into tidy categories (ids must match the tab blocks below).
const TAB_GROUPS = [
  { label:'Overview',    tabs:[['overview','Overview',LayoutDashboard]] },
  { label:'Growth',      tabs:[['waitlist','Waitlist',ClipboardList],['email','Email',Mail],['visits','Visits',Eye],['discovery','Discovery',Compass]] },
  { label:'People',      tabs:[['users','Users',Users],['connections','Connections',Link2],['agents','Support Agents',Headphones]] },
  { label:'Marketplace', tabs:[['vendors','Vendors',Store]] },
  { label:'Revenue',     tabs:[['payments','Payments',CreditCard]] },
  { label:'Content',     tabs:[['photos','Photos',Image],['testimonials','Testimonials',MessageSquareQuote]] },
  { label:'System',      tabs:[['actions','Actions',Zap],['settings','Settings',Settings],['features','Features',SlidersHorizontal]] },
]

// Round a max value up to a clean axis bound (5, 10, 20, 50, 100, …).
function niceCeil(n) {
  if (n <= 5) return 5
  const pow = Math.pow(10, Math.floor(Math.log10(n)))
  const f = n / pow
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * pow
}

// Lightweight bar chart with labelled X/Y axes + gridlines (no chart library).
// data: [{ label, value }].  yTitle/xTitle name what each axis represents.
// Stacked daily revenue: premium (terracotta) on commissions (green), per day.
function StackedRevenueChart({ data, height = 250 }) {
  const plotH   = height - 34
  const totals  = data.map(d => (d.premium || 0) + (d.commission || 0))
  const niceMax = niceCeil(Math.max(1, ...totals))
  const ticks   = 4
  const yTicks  = Array.from({ length: ticks + 1 }, (_, i) => Math.round(niceMax * (ticks - i) / ticks))
  const every   = Math.max(1, Math.ceil(data.length / 8))
  const fmtK    = (n) => n >= 1000 ? (n / 1000).toFixed(n % 1000 === 0 ? 0 : 1) + 'k' : '' + n
  const C = { premium: '#C4826A', commission: '#3D8B5E' }
  return (
    <div>
      <div style={{ display:'flex', alignItems:'stretch' }}>
        <div style={{ writingMode:'vertical-rl', transform:'rotate(180deg)', display:'flex', alignItems:'center', justifyContent:'center', height:plotH, fontFamily:'Nunito Sans, sans-serif', fontSize:'0.62rem', fontWeight:700, color:'#9B9B9B', paddingRight:'0.3rem' }}>Revenue (₦)</div>
        <div style={{ display:'flex', flexDirection:'column', justifyContent:'space-between', height:plotH, paddingRight:'0.5rem', textAlign:'right' }}>
          {yTicks.map((t, i) => <span key={i} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', color:'#BCBCBC', lineHeight:1 }}>{fmtK(t)}</span>)}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ position:'relative', height:plotH, borderLeft:'1px solid #E5E0D8', borderBottom:'1px solid #E5E0D8' }}>
            {yTicks.map((t, i) => i !== ticks && <div key={i} style={{ position:'absolute', left:0, right:0, top:`${(i / ticks) * 100}%`, borderTop:'1px dashed #F0EDE7' }}/>)}
            <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'flex-end', gap:2, padding:'0 2px' }}>
              {data.map((d, i) => {
                const prem = d.premium || 0, comm = d.commission || 0, tot = prem + comm
                return (
                  <div key={i} title={`${d.label} — premium ₦${prem.toLocaleString()} · commissions ₦${comm.toLocaleString()} · total ₦${tot.toLocaleString()}`}
                    style={{ flex:1, height:`${(tot / niceMax) * 100}%`, minHeight:tot > 0 ? 2 : 0, display:'flex', flexDirection:'column', justifyContent:'flex-end', borderRadius:'3px 3px 0 0', overflow:'hidden' }}>
                    <div style={{ height:`${tot > 0 ? (comm / tot) * 100 : 0}%`, background:C.commission }}/>
                    <div style={{ height:`${tot > 0 ? (prem / tot) * 100 : 0}%`, background:C.premium }}/>
                  </div>
                )
              })}
            </div>
          </div>
          <div style={{ display:'flex', gap:2, padding:'0.3rem 2px 0' }}>
            {data.map((d, i) => <span key={i} style={{ flex:1, textAlign:'center', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.55rem', color:'#BCBCBC', whiteSpace:'nowrap', overflow:'hidden' }}>{i % every === 0 ? d.label : ''}</span>)}
          </div>
        </div>
      </div>
      <div style={{ display:'flex', gap:'1.25rem', justifyContent:'center', marginTop:'0.5rem' }}>
        {[['Premium', C.premium], ['Commissions', C.commission]].map(([lab, col]) => (
          <span key={lab} style={{ display:'inline-flex', alignItems:'center', gap:'0.35rem', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#6B6B6B' }}>
            <span style={{ width:10, height:10, borderRadius:3, background:col }}/>{lab}
          </span>
        ))}
      </div>
    </div>
  )
}

function AxisBarChart({ data, color = '#C4826A', height = 230, yTitle = '', xTitle = '' }) {
  const plotH   = height - 34
  const niceMax = niceCeil(Math.max(1, ...data.map(d => d.value)))
  const ticks   = 4
  const yTicks  = Array.from({ length: ticks + 1 }, (_, i) => Math.round(niceMax * (ticks - i) / ticks))
  const every   = Math.max(1, Math.ceil(data.length / 8))
  const fmtK    = (n) => n >= 1000 ? (n / 1000).toFixed(n % 1000 === 0 ? 0 : 1) + 'k' : '' + n
  return (
    <div style={{ display:'flex', alignItems:'stretch' }}>
      {yTitle && (
        <div style={{ writingMode:'vertical-rl', transform:'rotate(180deg)', display:'flex', alignItems:'center', justifyContent:'center', height:plotH, fontFamily:'Nunito Sans, sans-serif', fontSize:'0.62rem', fontWeight:700, color:'#9B9B9B', paddingRight:'0.3rem' }}>{yTitle}</div>
      )}
      <div style={{ display:'flex', flexDirection:'column', justifyContent:'space-between', height:plotH, paddingRight:'0.5rem', textAlign:'right' }}>
        {yTicks.map((t, i) => <span key={i} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', color:'#BCBCBC', lineHeight:1 }}>{fmtK(t)}</span>)}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ position:'relative', height:plotH, borderLeft:'1px solid #E5E0D8', borderBottom:'1px solid #E5E0D8' }}>
          {yTicks.map((t, i) => i !== ticks && <div key={i} style={{ position:'absolute', left:0, right:0, top:`${(i / ticks) * 100}%`, borderTop:'1px dashed #F0EDE7' }}/>)}
          <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'flex-end', gap:2, padding:'0 2px' }}>
            {data.map((d, i) => (
              <div key={i} title={`${d.label}: ${d.value.toLocaleString()}`} style={{ flex:1, height:`${(d.value / niceMax) * 100}%`, minHeight:d.value > 0 ? 2 : 0, background:color, borderRadius:'3px 3px 0 0', opacity:0.92 }}/>
            ))}
          </div>
        </div>
        <div style={{ display:'flex', gap:2, padding:'0.3rem 2px 0' }}>
          {data.map((d, i) => <span key={i} style={{ flex:1, textAlign:'center', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.55rem', color:'#BCBCBC', whiteSpace:'nowrap', overflow:'hidden' }}>{i % every === 0 ? d.label : ''}</span>)}
        </div>
        {xTitle && <p style={{ textAlign:'center', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.62rem', fontWeight:700, color:'#9B9B9B', margin:'0.25rem 0 0' }}>{xTitle}</p>}
      </div>
    </div>
  )
}

// Turn the admin's plain text into safe HTML: escapes, then **bold**, [links](url),
// blank lines → paragraphs, single newlines → <br>. {{name}} is left as a token.
function campMdToHtml(text) {
  const esc = s => s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
  const blocks = (text || '').trim().split(/\n{2,}/).map(block => {
    let h = esc(block)
    h = h.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" style="color:#C4826A;text-decoration:underline;">$1</a>')
    h = h.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    h = h.replace(/\n/g, '<br/>')
    return `<p style="margin:0 0 16px;">${h}</p>`
  })
  return blocks.join('') || '<p style="margin:0;">&nbsp;</p>'
}

// Wrap the message in the branded Okoiya email template. The admin dashboard
// preview and the email-campaign edge function both use this exact output.
function campComposeEmail(body) {
  return `<!DOCTYPE html><html><body style="margin:0;padding:0;background:#FAFAF8;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;color:#1A1A1A;">
    <table width="100%" cellpadding="0" cellspacing="0" style="background:#FAFAF8;padding:40px 16px;"><tr><td align="center">
      <table width="100%" style="max-width:520px;background:#fff;border:1px solid #E5E0D8;border-radius:16px;overflow:hidden;">
        <tr><td style="background:#FAFAF8;padding:20px 40px;text-align:center;border-bottom:1px solid #EDE7DF;"><img src="${(typeof window!=='undefined'?window.location.origin:'https://okoiya.com')}/okoiya-logo.png" alt="Okoiya" height="30" style="height:30px;display:inline-block;border:0;outline:none;text-decoration:none;"/></td></tr>
        <tr><td style="padding:36px 40px;font-size:15px;line-height:1.7;color:#3A3A3A;">
          ${campMdToHtml(body)}
          <p style="margin:24px 0 0;color:#9B9B9B;">With love,<br/>The Okoiya Team</p>
        </td></tr>
        <tr><td style="padding:0 40px 28px;"><p style="margin:0;font-size:11px;color:#BCBCBC;text-align:center;line-height:1.6;">You're receiving this because you joined Okoiya or our waitlist.</p></td></tr>
      </table>
    </td></tr></table></body></html>`
}

export default function AdminPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [stats, setStats]             = useState(null)
  const [users, setUsers]             = useState([])
  const [payments, setPayments]       = useState([])
  const [weddings, setWeddings]       = useState([])
  const [messages, setMessages]       = useState([])
  const [discoveryCounts, setDiscoveryCounts] = useState({})
  const [agents,    setAgents]   = useState([])
  const [agentEmail, setAgentEmail] = useState('')
  const [agentName,  setAgentName]  = useState('')
  const [agentSaving, setAgentSaving] = useState(false)
  const [loading, setLoading]         = useState(true)
  const [tab, setTab]                 = useState('overview')
  const [sidebarOpen, setSidebarOpen] = useState(false)   // mobile drawer

  const [redFlags, setRedFlags]       = useState({ paidButFree: [] })
  const [linkedCouples, setLinkedCouples] = useState([])
  const [pendingVendors, setPendingVendors] = useState([])
  const [rejectingId, setRejectingId] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [visits, setVisits]           = useState([])
  const [guestPhotos, setGuestPhotos] = useState([])
  const [maintEnabled, setMaintEnabled] = useState(false)
  const [maintMsg, setMaintMsg]         = useState('')
  const [signupsPaused, setSignupsPaused] = useState(false)
  const [siteLive, setSiteLive]           = useState(true)
  const [launchAtInput, setLaunchAtInput] = useState('')
  const [revView, setRevView]             = useState(null)   // 'test' | 'real' (payments tab)
  const [liveAtInput, setLiveAtInput]     = useState('')
  const [recentTx, setRecentTx]           = useState([])     // combined ledger
  const [chartDays, setChartDays]         = useState(30)     // revenue chart range
  const [txFilter, setTxFilter]           = useState('all')  // all|premium|gift|asoebi
  const [showPaySettings, setShowPaySettings] = useState(false)
  const [featureFlags, setFeatureFlags]   = useState(FLAG_DEFAULTS)
  const [waitlist, setWaitlist]           = useState([])
  const [waitlistFilter, setWaitlistFilter] = useState('all')
  const [userSearch, setUserSearch]       = useState('')
  const [userPlanFilter, setUserPlanFilter] = useState('all')
  const [campSubject, setCampSubject]     = useState('')
  const [campBody, setCampBody]           = useState('')
  const [campAudience, setCampAudience]   = useState('all')
  const [campBusy, setCampBusy]           = useState(false)
  const [campMsg, setCampMsg]             = useState('')

  // Gated admin actions (re-auth + 2FA)
  const [actionEmail, setActionEmail] = useState('')
  const [actionMsg, setActionMsg]     = useState('')
  const [reauth, setReauth]           = useState(null)  // { payload, label }
  const [reauthPw, setReauthPw]       = useState('')
  const [reauthCode, setReauthCode]   = useState('')
  const [reauthErr, setReauthErr]     = useState('')
  const [reauthBusy, setReauthBusy]   = useState(false)

  // Admin manager + 2FA
  const [authorized, setAuthorized]   = useState(null)  // null=checking, true, false
  const [admins, setAdmins]           = useState([])
  const [superAdmins, setSuperAdmins] = useState([])
  const [my2fa, setMy2fa]             = useState(false)
  const [meEmail, setMeEmail]         = useState('')
  const [addAdminEmail, setAddAdminEmail] = useState('')
  const [addAdminName, setAddAdminName]   = useState('')
  const [enrollData, setEnrollData]   = useState(null)  // { factorId, qr, secret }
  const [enrollCode, setEnrollCode]   = useState('')
  const [enrollBusy, setEnrollBusy]   = useState(false)
  const [enrollErr, setEnrollErr]     = useState('')

  useEffect(() => {
    fetchData()
    fetchAgents()
    fetchWaitlist()
  }, [])

  async function fetchWaitlist() {
    const { data } = await supabase.rpc('admin_list_waitlist')
    setWaitlist(data || [])
  }

  async function syncFounding() {
    const { data, error } = await supabase.rpc('sync_founding_couples')
    if (error) { window.alert('Sync failed: ' + error.message); return }
    window.alert(`${data ?? 0} founding ${data === 1 ? 'couple' : 'couples'} flagged. Their portals now show the Founding Couple badge.`)
  }

  async function callWaitlistBlast(payload) {
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch(import.meta.env.VITE_SUPABASE_URL + '/functions/v1/waitlist-blast', {
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    return { ok: res.ok, data: await res.json().catch(() => ({})) }
  }

  async function sendLaunchTest() {
    const { ok, data } = await callWaitlistBlast({ test: true })
    window.alert(ok && data.sent ? `Preview sent to ${meEmail}. Check your inbox.` : 'Could not send the test: ' + (data.error || 'unknown error'))
  }

  async function callEmailCampaign(payload) {
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch(import.meta.env.VITE_SUPABASE_URL + '/functions/v1/email-campaign', {
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    return { ok: res.ok, data: await res.json().catch(() => ({})) }
  }

  async function sendCampaignTest() {
    if (!campSubject.trim() || !campBody.trim()) { setCampMsg('Add a subject and a message first.'); return }
    setCampBusy(true); setCampMsg('Sending test…')
    const { ok, data } = await callEmailCampaign({ test: true, subject: campSubject.trim(), html: campComposeEmail(campBody) })
    setCampMsg(ok && data.sent ? `Test sent to ${meEmail} — check your inbox.` : 'Test failed: ' + (data.error || 'unknown error'))
    setCampBusy(false)
  }

  async function sendCampaign() {
    if (!campSubject.trim() || !campBody.trim()) { setCampMsg('Add a subject and a message first.'); return }
    const who = campAudience === 'waitlist' ? 'waitlist couples' : campAudience === 'users' ? 'signed-up users' : 'EVERYONE (waitlist + users)'
    if (!window.confirm(`Send "${campSubject.trim()}" to ${who}?\n\nThis sends REAL emails. Send a test to yourself first if you haven't.`)) return
    setCampBusy(true); setCampMsg('Sending…')
    const html = campComposeEmail(campBody)
    let offset = 0, totalSent = 0, total = 0
    for (let guard = 0; guard < 500; guard++) {
      const { ok, data } = await callEmailCampaign({ subject: campSubject.trim(), html, audience: campAudience, offset })
      if (!ok) { setCampMsg('Send failed: ' + (data.error || 'unknown error')); setCampBusy(false); return }
      totalSent += data.sent || 0; total = data.total || total
      setCampMsg(`Sent ${totalSent} of ${total}…`)
      if (data.nextOffset == null) break
      offset = data.nextOffset
    }
    setCampMsg(`Done — sent ${totalSent} email${totalSent === 1 ? '' : 's'} 🎉`)
    setCampBusy(false)
  }

  async function sendLaunchBlast() {
    const pending = waitlist.filter(l => l.wants_updates !== false && !l.notified_at).length
    if (!window.confirm(`Send the launch announcement to ${pending} waitlist couple${pending === 1 ? '' : 's'} who haven't been emailed yet?\n\nThis sends REAL emails. Send a test to yourself first if you haven't.`)) return
    const { ok, data } = await callWaitlistBlast({})
    if (!ok) { window.alert('Blast failed: ' + (data.error || 'unknown error')); return }
    window.alert(`Sent ${data.sent}. ${data.remaining} still to go.` + (data.remaining > 0 ? '\nClick "Send launch emails" again to send the next batch.' : '\nEveryone has been notified 🎉'))
    fetchWaitlist()
  }

  async function deleteLead(id) {
    if (!window.confirm('Delete this lead permanently? This cannot be undone.')) return
    setWaitlist(prev => prev.filter(l => l.id !== id))
    await supabase.rpc('admin_delete_waitlist', { p_id: id })
    fetchWaitlist()   // founding tiers re-rank server-side — refresh to show it
  }

  async function setLeadStatus(id, status) {
    setWaitlist(prev => prev.map(l => l.id === id ? { ...l, status } : l))
    await supabase.rpc('admin_set_waitlist_status', { p_id: id, p_status: status })
    fetchWaitlist()   // dropping/restoring reshuffles founding tiers
  }

  function exportWaitlistCsv() {
    const cols = ['created_at','name','partner_name','email','phone','location','country','wedding_date','guest_estimate','biggest_challenge','heard_from','wants_updates','tier','status']
    const esc = v => `"${String(v ?? '').replace(/"/g,'""')}"`
    const rows = [cols.join(',')].concat(waitlist.map(l => cols.map(c => esc(l[c])).join(',')))
    const blob = new Blob([rows.join('\n')], { type:'text/csv' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `okoiya-waitlist-${new Date().toISOString().slice(0,10)}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  async function fetchData() {
    setLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(
        import.meta.env.VITE_SUPABASE_URL + '/functions/v1/admin-stats',
        { headers: { 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' } }
      )
      if (res.status === 403) { setAuthorized(false); navigate('/dashboard'); return }
      if (!res.ok) throw new Error('Failed to fetch admin data')
      const data = await res.json()
      setAuthorized(true)
      setStats(data.stats)
      setUsers(data.users || [])
      setPayments(data.payments || [])
      setRecentTx(data.recentTransactions || [])
      setWeddings(data.weddings || [])
      setDiscoveryCounts(data.discoveryCounts || {})
      setRedFlags(data.redFlags || { paidButFree: [] })
      setLinkedCouples(data.linkedCouples || [])
      setPendingVendors(data.pendingVendors || [])
      setVisits(data.visits || [])
      setGuestPhotos(data.guestPhotos || [])
      setAdmins(data.admins || [])
      setSuperAdmins(data.superAdmins || [])
      setMy2fa(!!data.me?.mfa_enabled)
      setMeEmail(data.me?.email || '')
      const mt = data.settings?.maintenance || {}
      setMaintEnabled(!!mt.enabled); setMaintMsg(mt.message || '')
      setSignupsPaused(!!(data.settings?.signups_paused?.enabled))
      setSiteLive(data.settings?.launch?.live !== false)
      setFeatureFlags({ ...FLAG_DEFAULTS, ...(data.settings?.feature_flags || {}) })
      // Launch countdown time (ISO string) → datetime-local input value
      const la = data.settings?.launch_at
      if (la) { const d = new Date(la); if (!isNaN(d)) setLaunchAtInput(new Date(d.getTime() - d.getTimezoneOffset()*60000).toISOString().slice(0,16)) }
      else setLaunchAtInput('')
    } catch(e) {
      console.error('Admin fetch error:', e)
    }
    setLoading(false)
  }

  // ── TOTP two-factor (Supabase native MFA) ──
  async function enroll2fa() {
    setEnrollErr(''); setEnrollBusy(true)
    try {
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'Okoiya Admin ' + Date.now() })
      if (error) throw error
      setEnrollData({ factorId: data.id, qr: data.totp.qr_code, secret: data.totp.secret })
      setEnrollCode('')
    } catch(e) { setEnrollErr(e.message || 'Could not start enrollment') }
    setEnrollBusy(false)
  }
  async function verifyEnroll() {
    if (enrollCode.trim().length !== 6) { setEnrollErr('Enter the 6-digit code'); return }
    setEnrollBusy(true); setEnrollErr('')
    try {
      const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({ factorId: enrollData.factorId })
      if (chErr) throw chErr
      const { error: vErr } = await supabase.auth.mfa.verify({ factorId: enrollData.factorId, challengeId: ch.id, code: enrollCode.trim() })
      if (vErr) throw new Error('Incorrect code — use the current one from your app')
      await callAdminAction({ action: 'set_2fa', value: true })
      setMy2fa(true); setEnrollData(null); setEnrollCode(''); setActionMsg('Two-factor authentication enabled')
    } catch(e) { setEnrollErr(e.message) }
    setEnrollBusy(false)
  }
  async function disable2fa() {
    setEnrollBusy(true); setEnrollErr('')
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors()
      for (const f of (factors?.totp || [])) { if (f.id) await supabase.auth.mfa.unenroll({ factorId: f.id }) }
      await callAdminAction({ action: 'set_2fa', value: false })
      setMy2fa(false); setActionMsg('Two-factor authentication disabled')
    } catch(e) { setEnrollErr(e.message) }
    setEnrollBusy(false)
  }

  async function fetchAgents() {
    const [{ data: agentData }, { data: resolvedTickets }] = await Promise.all([
      supabase.from('cs_agents').select('*').order('created_at', { ascending: false }),
      supabase.from('support_tickets').select('assigned_to').eq('status', 'resolved'),
    ])
    const resolvedCounts = {}
    for (const t of resolvedTickets || []) {
      if (t.assigned_to) resolvedCounts[t.assigned_to] = (resolvedCounts[t.assigned_to] || 0) + 1
    }
    setAgents((agentData || []).map(a => ({ ...a, resolvedCount: resolvedCounts[a.email] || 0 })))
  }

  async function callAdminAction(payload, password) {
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch(import.meta.env.VITE_SUPABASE_URL + '/functions/v1/admin-action', {
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, password }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || 'Action failed')
    return data
  }
  function requestAction(payload, label) {
    setActionMsg(''); setReauthErr(''); setReauthPw(''); setReauthCode(''); setReauth({ payload, label })
  }
  async function moderatePhoto(id, hidden) {
    setGuestPhotos(prev => prev.map(p => p.id === id ? { ...p, hidden } : p))
    try {
      await callAdminAction({ action: 'set_photo_hidden', photo_id: id, value: hidden })
    } catch (e) {
      setGuestPhotos(prev => prev.map(p => p.id === id ? { ...p, hidden: !hidden } : p))
    }
  }
  async function reviewVendor(id, status, reason) {
    setPendingVendors(prev => prev.filter(v => v.id !== id))  // optimistic remove
    setRejectingId(null); setRejectReason('')
    try {
      await callAdminAction({ action: 'set_vendor_status', vendor_id: id, status, reason })
    } catch (e) {
      fetchData()  // restore on failure
    }
  }

  async function confirmReauth() {
    if (!reauthPw) { setReauthErr('Enter your password'); return }
    if (my2fa && reauthCode.trim().length !== 6) { setReauthErr('Enter your 6-digit code'); return }
    setReauthBusy(true); setReauthErr('')
    try {
      // If 2FA is on, elevate this session to aal2 with a fresh TOTP code first.
      if (my2fa) {
        const { data: factors } = await supabase.auth.mfa.listFactors()
        const totp = factors?.totp?.[0]
        if (!totp) throw new Error('No 2FA device found — re-enable it in Settings')
        const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({ factorId: totp.id })
        if (chErr) throw chErr
        const { error: vErr } = await supabase.auth.mfa.verify({ factorId: totp.id, challengeId: ch.id, code: reauthCode.trim() })
        if (vErr) throw new Error('Incorrect 2FA code')
      }
      const data = await callAdminAction(reauth.payload, reauthPw)
      setActionMsg(data.message || 'Done')
      setReauth(null); setReauthPw(''); setReauthCode('')
      fetchData()
    } catch (e) { setReauthErr(e.message) }
    setReauthBusy(false)
  }

  function submitAddAdmin() {
    const e = addAdminEmail.trim().toLowerCase()
    if (!e || !e.includes('@')) { setActionMsg(''); return }
    requestAction({ action: 'add_admin', email: e, name: addAdminName.trim() || null }, `Add ${e} as an admin`)
    setAddAdminEmail(''); setAddAdminName('')
  }

  if (authorized === null) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#FAFAF8', fontFamily:'Nunito Sans, sans-serif', color:'#BCBCBC' }}>Checking access…</div>
  )
  if (authorized === false) return null

  const fmt = (n) => `₦${Number(n||0).toLocaleString()}`
  const CUR = { NGN:'₦', USD:'$', GBP:'£', EUR:'€', GHS:'₵', KES:'KSh ', ZAR:'R ', CAD:'C$', AUD:'A$' }
  const fmtCur = (n, cur) => `${CUR[cur] || (cur ? cur+' ' : '₦')}${Number(n||0).toLocaleString()}`
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '—'
  // Headline revenue extras (active mode) for the overview cards
  const rMode   = stats?.revenueMode || 'test'
  const rCounts = stats?.revenueCounts?.[rMode] || {}
  const rTxns   = (rCounts.premium||0) + (rCounts.gift||0) + (rCounts.asoebi||0)
  const rGmv    = stats?.revenueGMV?.[rMode] ? ((stats.revenueGMV[rMode].gift||0) + (stats.revenueGMV[rMode].asoebi||0)) : 0

  const pageTitle = (TAB_GROUPS.flatMap(g => g.tabs).find(t => t[0] === tab) || [null, 'Overview'])[1]

  return (
    <div className="admin-shell" style={{ background:'#F4F4F6', display:'flex' }}>
      <style>{`
        .admin-shell{ height:100vh; height:100dvh; overflow:hidden; }
        .admin-navbtn:hover{ background:#F3F0EA !important; }
        .admin-navbtn.active:hover{ background:rgba(196,130,106,0.12) !important; }
        .admin-hamburger{ display:none; }
        .admin-sidebar-close{ display:none; }
        .admin-backdrop{ position:fixed; inset:0; background:rgba(0,0,0,0.42); z-index:55; display:none; }
        @media (max-width:820px){
          .admin-hamburger{ display:inline-flex !important; }
          .admin-sidebar-close{ display:inline-flex !important; }
          .admin-backdrop{ display:block; }
          .admin-sidebar{
            position:fixed !important; top:0 !important; left:0 !important; bottom:0 !important;
            z-index:60 !important; width:264px !important; min-height:100vh !important;
            transform:translateX(-100%); transition:transform 0.25s ease; box-shadow:0 12px 44px rgba(0,0,0,0.18);
          }
          .admin-sidebar.open{ transform:translateX(0) !important; }
        }
      `}</style>

      {/* Mobile backdrop */}
      {sidebarOpen && <div className="admin-backdrop" onClick={()=>setSidebarOpen(false)}/>}

      {/* ── Sidebar ── */}
      <aside className={`admin-sidebar${sidebarOpen?' open':''}`} style={{ width:236, flexShrink:0, background:'#fff', borderRight:'1px solid #E9E9EE', height:'100%', display:'flex', flexDirection:'column', padding:'1.25rem 0' }}>
        <div style={{ padding:'0 1.25rem 1.1rem', display:'flex', alignItems:'center', gap:'0.55rem' }}>
          <img src="/okoiya-logo.png" alt="Okoiya" style={{ height:30, objectFit:'contain' }}/>
          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', fontWeight:800, letterSpacing:'0.08em', color:'#C4826A', background:'rgba(196,130,106,0.1)', borderRadius:100, padding:'2px 8px' }}>ADMIN</span>
          <button className="admin-sidebar-close" onClick={()=>setSidebarOpen(false)} aria-label="Close menu" style={{ marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color:'#9B9B9B', padding:4 }}>
            <X size={20}/>
          </button>
        </div>
        <nav className="admin-nav" style={{ flex:1, overflowY:'auto', padding:'0 0.7rem' }}>
          {TAB_GROUPS.map(g => (
            <div key={g.label} className="admin-navgroup" style={{ marginBottom:'0.9rem' }}>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.56rem', fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', color:'#BCBCBC', margin:'0 0 0.3rem 0.6rem', whiteSpace:'nowrap' }}>{g.label}</p>
              {g.tabs.map(([id, label, Icon]) => (
                <button key={id} onClick={()=>{ setTab(id); setSidebarOpen(false) }} className={`admin-navbtn${tab===id?' active':''}`} style={{
                  display:'flex', alignItems:'center', gap:'0.6rem', width:'100%', textAlign:'left',
                  padding:'0.5rem 0.7rem', borderRadius:'0.6rem', border:'none', cursor:'pointer', marginBottom:2,
                  fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', fontWeight:tab===id?700:600, whiteSpace:'nowrap',
                  color:tab===id?'#C4826A':'#5A5A5A', background:tab===id?'rgba(196,130,106,0.1)':'transparent', transition:'background 0.15s, color 0.15s',
                }}>
                  <Icon size={16} color={tab===id?'#C4826A':'#9B9B9B'}/> {label}
                </button>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      {/* ── Main ── */}
      <main style={{ flex:1, minWidth:0, height:'100%', display:'flex', flexDirection:'column' }}>
        <div style={{ background:'#fff', borderBottom:'1px solid #E9E9EE', padding:'1rem 1.75rem', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem', flexWrap:'wrap', flexShrink:0, zIndex:20 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
            <button className="admin-hamburger" onClick={()=>setSidebarOpen(true)} aria-label="Open menu" style={{ background:'none', border:'1px solid #E9E9EE', borderRadius:'0.6rem', cursor:'pointer', color:'#5A5A5A', padding:'0.4rem', alignItems:'center', justifyContent:'center' }}>
              <Menu size={20}/>
            </button>
            <div>
              <h1 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.5rem', margin:'0 0 0.1rem' }}>{pageTitle}</h1>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.76rem', color:'#9B9B9B', margin:0 }}>{meEmail ? `Signed in as ${meEmail}` : 'Okoiya platform'}</p>
            </div>
          </div>
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <button onClick={fetchData} className="btn-secondary" style={{ fontSize:'0.8rem', padding:'0.55rem 1rem', display:'flex', alignItems:'center', gap:'0.4rem' }}>
              <RefreshCw size={14}/> Refresh
            </button>
            <button onClick={()=>navigate('/dashboard')} className="btn-secondary" style={{ fontSize:'0.8rem', padding:'0.55rem 1rem' }}>← Back</button>
          </div>
        </div>

        <div style={{ padding:'1.75rem', flex:1, overflowY:'auto', minHeight:0, WebkitOverflowScrolling:'touch' }}>

        {loading ? (
          <p style={{ textAlign:'center', color:'#BCBCBC', padding:'3rem' }}>Loading admin data…</p>
        ) : stats && (
          <>
            {/* Overview */}
            {tab==='overview' && (
              <div>
                {/* Stat cards */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(210px, 1fr))', gap:'0.875rem', marginBottom:'1.25rem' }}>
                  {[
                    { label:'Total Users',     value:stats.total,                 icon:Users,         color:'#C4826A', sub:`${stats.premium} premium · ${stats.free} free` },
                    { label:'Premium Couples', value:stats.premium,               icon:Crown,         color:'#9BAF9B', sub:`${stats.total>0?Math.round(stats.premium/stats.total*100):0}% of users` },
                    { label:'Weddings',        value:stats.weddings,              icon:Heart,         color:'#8B7BB8', sub:`${stats.todaySignups} new today` },
                    { label: stats.revenueMode==='real' ? 'Revenue · LIVE' : 'Revenue · test', value:fmt(stats.revenue), icon:TrendingUp, color:'#3D8B5E', sub:`${rTxns} txns · ${fmt(stats.revenuePremium)} prem · ${fmt(stats.revenueCommission)} comm` },
                    { label:'Raised for couples', value:fmt(rGmv),                icon:CreditCard,    color:'#C4826A', sub:'gross gifts + aso ebi' },
                    { label:'Signups Today',   value:stats.todaySignups,          icon:Calendar,      color:'#C4826A', sub:`${stats.thisMonthSignups} this month` },
                    { label:'Visits Today',    value:stats.visitsToday ?? 0,      icon:Eye,           color:'#9BAF9B', sub:`${stats.visits30 ?? 0} in 30 days` },
                    { label:'Waitlist',        value:waitlist.length,             icon:ClipboardList, color:'#8B7BB8', sub:`${waitlist.filter(l=>l.tier&&l.tier!=='standard').length} founding tier` },
                    { label:'Pending Vendors', value:pendingVendors.length,       icon:Store,         color:'#B45309', sub:'awaiting review' },
                  ].map(s=>(
                    <div key={s.label} style={{ background:'#fff', border:'1px solid #ECECEF', borderRadius:'1rem', padding:'1.1rem 1.25rem', display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'0.75rem' }}>
                      <div style={{ minWidth:0 }}>
                        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', fontWeight:600, margin:'0 0 0.3rem' }}>{s.label}</p>
                        <p style={{ fontFamily:'Gibs, serif', fontSize:'1.7rem', color:'#1A1A1A', margin:0, fontWeight:400, lineHeight:1 }}>{s.value}</p>
                        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#BCBCBC', margin:'0.4rem 0 0' }}>{s.sub}</p>
                      </div>
                      <div style={{ width:42, height:42, borderRadius:'50%', background:`${s.color}1F`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <s.icon size={19} color={s.color}/>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Traffic chart */}
                <div style={{ background:'#fff', border:'1px solid #ECECEF', borderRadius:'1rem', padding:'1.25rem 1.35rem', marginBottom:'1.5rem' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1rem', flexWrap:'wrap', gap:'0.5rem' }}>
                    <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.1rem', margin:0 }}>Website traffic</h3>
                    <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B' }}>{stats.visitsToday ?? 0} today · {stats.visits30 ?? 0} in 30 days</span>
                  </div>
                  {visits.length === 0
                    ? <p style={{ color:'#BCBCBC', textAlign:'center', padding:'2rem', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem' }}>No visits recorded yet</p>
                    : <AxisBarChart data={visits.map(v=>({ label:fmtDate(v.day).replace(/ \d{4}$/,''), value:v.count||0 }))} color="#C4826A" yTitle="Visits" xTitle="Date (last 30 days)"/>}
                </div>

                {redFlags.paidButFree?.length > 0 && (
                  <div style={{ background:'rgba(220,38,38,0.05)', border:'1px solid rgba(220,38,38,0.25)', borderRadius:'0.875rem', padding:'1rem 1.125rem', marginBottom:'1.5rem' }}>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', color:'#DC2626', margin:'0 0 0.25rem' }}>⚠ {redFlags.paidButFree.length} paid but still free</p>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#6B6B6B', margin:'0 0 0.75rem', lineHeight:1.5 }}>These couples have a verified payment but aren't on premium. Grant them premium from the <strong>Actions</strong> tab.</p>
                    <div style={{ display:'flex', flexDirection:'column', gap:'0.35rem' }}>
                      {redFlags.paidButFree.map(w=>(
                        <div key={w.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background:'white', border:'1px solid #F0E0E0', borderRadius:'0.625rem', padding:'0.5rem 0.75rem' }}>
                          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', fontWeight:600, color:'#1A1A1A' }}>{w.partner1_name} & {w.partner2_name}</span>
                          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'#BCBCBC' }}>/w/{w.slug}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.1rem', margin:'0 0 1rem' }}>Recent Weddings</h3>
                <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                  {weddings.slice(0,15).map(w=>(
                    <div key={w.id} className="card" style={{ padding:'0.75rem 1rem', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <div style={{ minWidth:0 }}>
                        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', margin:0 }}>
                          {w.partner1_name} & {w.partner2_name}
                          {w.linked
                            ? <span style={{ marginLeft:6, fontSize:'0.6rem', fontWeight:700, color:'#9BAF9B', background:'rgba(155,175,155,0.12)', borderRadius:100, padding:'1px 7px' }}>🔗 linked</span>
                            : <span style={{ marginLeft:6, fontSize:'0.6rem', fontWeight:700, color:'#BCBCBC', background:'#F3F0EA', borderRadius:100, padding:'1px 7px' }}>solo</span>}
                        </p>
                        {(w.partner1Email || w.partner2Email) && (
                          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#6B6B6B', margin:'0.15rem 0 0' }}>
                            {w.partner1Email || '—'}{w.partner2Email ? ` ✚ ${w.partner2Email}` : ''}
                          </p>
                        )}
                        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#BCBCBC', margin:'0.1rem 0 0' }}>/w/{w.slug}</p>
                      </div>
                      <div style={{ textAlign:'right' }}>
                        <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.65rem', fontWeight:700, color:w.plan==='premium'?'#9BAF9B':'#BCBCBC', background:w.plan==='premium'?'rgba(155,175,155,0.1)':'#F3F0EA', borderRadius:100, padding:'2px 8px' }}>{w.plan}</span>
                        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.65rem', color:'#BCBCBC', margin:'0.2rem 0 0' }}>{fmtDate(w.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Features — page/feature toggles (hidden everywhere when off) */}
            {tab==='features' && (
              <div style={{ maxWidth:560 }}>
                <div className="card" style={{ padding:'1.25rem' }}>
                  <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.1rem', margin:'0 0 0.3rem' }}>Feature toggles</h3>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#9B9B9B', margin:'0 0 1.25rem', lineHeight:1.6 }}>
                    Turn a page or feature off and it vanishes for everyone — the nav item disappears and the route redirects, so users never know it existed. Saving needs your password{my2fa ? ' + 2FA code' : ''}.
                  </p>
                  <div style={{ display:'flex', flexDirection:'column', gap:'0.875rem' }}>
                    {Object.keys(FLAG_DEFAULTS).map(key => (
                      <div key={key} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem', borderBottom:'1px solid #F3F0EA', paddingBottom:'0.75rem' }}>
                        <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', color:'#1A1A1A' }}>{FLAG_LABELS[key] || key}</span>
                        <ToggleBtn on={featureFlags[key] !== false} onClick={()=>setFeatureFlags(f => ({ ...f, [key]: !(f[key] !== false) }))} />
                      </div>
                    ))}
                  </div>
                  <button onClick={()=>requestAction({ action:'set_setting', key:'feature_flags', value: featureFlags }, 'Update feature toggles')}
                    style={{ marginTop:'1.25rem', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.8rem', color:'white', background:'#C4826A', border:'none', borderRadius:100, padding:'0.6rem 1.5rem', cursor:'pointer' }}>
                    Save toggles
                  </button>
                  {actionMsg && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#3D8B5E', margin:'0.875rem 0 0', fontWeight:600 }}>{actionMsg}</p>}
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#BCBCBC', margin:'0.75rem 0 0' }}>Note: changes show for users on their next page load (flags are cached per session).</p>
                </div>
              </div>
            )}

            {/* Waitlist — Founding Couples leads */}
            {tab==='waitlist' && (() => {
              const STATUSES = ['new','contacted','converted','dropped']
              const TONE = {
                new:       { c:'#C4826A', bg:'rgba(196,130,106,0.1)' },
                contacted: { c:'#B45309', bg:'rgba(217,119,6,0.1)' },
                converted: { c:'#3D8B5E', bg:'rgba(61,139,94,0.1)' },
                dropped:   { c:'#9B9B9B', bg:'#F3F0EA' },
              }
              const counts = STATUSES.reduce((a,s)=>({ ...a, [s]: waitlist.filter(l=>l.status===s).length }), {})
              const rows = waitlistFilter==='all' ? waitlist : waitlist.filter(l=>l.status===waitlistFilter)
              return (
                <div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'0.75rem', marginBottom:'1rem' }}>
                    <div>
                      <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.2rem', margin:0 }}>Founding Couples Waitlist</h3>
                      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#9B9B9B', margin:'0.2rem 0 0' }}>{waitlist.length} total · {waitlist.filter(l=>l.tier==='lifetime').length}/3 free-for-life · {waitlist.filter(l=>l.tier==='discount').length}/22 at 30% off · {waitlist.filter(l=>l.notified_at).length} emailed · {counts.converted} converted</p>
                    </div>
                    <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                      <button onClick={fetchWaitlist} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:600, color:'#6B6B6B', background:'white', border:'1px solid #E5E0D8', borderRadius:100, padding:'0.45rem 0.9rem', cursor:'pointer', display:'inline-flex', alignItems:'center', gap:'0.35rem' }}><RefreshCw size={13}/> Refresh</button>
                      <button onClick={syncFounding} title="Flag founding couples (first-25 leads who've created a wedding) so their portals show the badge" style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:700, color:'#8A6D3B', background:'rgba(212,175,55,0.14)', border:'1px solid rgba(212,175,55,0.4)', borderRadius:100, padding:'0.45rem 0.9rem', cursor:'pointer', display:'inline-flex', alignItems:'center', gap:'0.35rem' }}><Crown size={13}/> Sync founding badges</button>
                      <button onClick={exportWaitlistCsv} disabled={!waitlist.length} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:700, color:'white', background:'#C4826A', border:'none', borderRadius:100, padding:'0.45rem 0.9rem', cursor:waitlist.length?'pointer':'default', opacity:waitlist.length?1:0.5 }}>Export CSV</button>
                      <button onClick={sendLaunchTest} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:600, color:'#6B6B6B', background:'white', border:'1px solid #E5E0D8', borderRadius:100, padding:'0.45rem 0.9rem', cursor:'pointer' }}>Send test to me</button>
                      <button onClick={sendLaunchBlast} disabled={!waitlist.length} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:700, color:'white', background:'#1A1A1A', border:'none', borderRadius:100, padding:'0.45rem 0.9rem', cursor:waitlist.length?'pointer':'default', opacity:waitlist.length?1:0.5, display:'inline-flex', alignItems:'center', gap:'0.35rem' }}>📣 Send launch emails</button>
                    </div>
                  </div>

                  {/* Launch countdown — drives the waitlist timer + auto-emails */}
                  <div className="card" style={{ padding:'1.1rem 1.25rem', marginBottom:'1rem', background:'linear-gradient(135deg,#FBF4EE,#F6EFE8)', border:'1px solid rgba(196,130,106,0.25)' }}>
                    <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', margin:'0 0 0.2rem' }}>⏳ Launch countdown</h3>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#6B6B6B', margin:'0 0 0.75rem', lineHeight:1.5 }}>
                      The waitlist page counts down to this time. When it passes, the launch email is sent automatically to everyone (the scheduler handles it). Leave blank to hide the countdown.
                    </p>
                    <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap', alignItems:'center' }}>
                      <input type="datetime-local" value={launchAtInput} onChange={e=>setLaunchAtInput(e.target.value)}
                        style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', border:'1px solid #E5E0D8', borderRadius:'0.75rem', padding:'0.55rem 0.875rem', outline:'none' }}/>
                      <button onClick={()=>requestAction({ action:'set_setting', key:'launch_at', value: launchAtInput ? new Date(launchAtInput).toISOString() : null }, launchAtInput ? 'Set the launch countdown time' : 'Clear the launch countdown')}
                        style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.8rem', color:'white', background:'#C4826A', border:'none', borderRadius:100, padding:'0.55rem 1.25rem', cursor:'pointer' }}>Save launch time</button>
                      {launchAtInput && (
                        <button onClick={()=>{ setLaunchAtInput(''); requestAction({ action:'set_setting', key:'launch_at', value:null }, 'Clear the launch countdown') }}
                          style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.8rem', color:'#9B9B9B', background:'none', border:'1px solid #E5E0D8', borderRadius:100, padding:'0.55rem 1rem', cursor:'pointer' }}>Clear</button>
                      )}
                    </div>
                  </div>

                  {/* Status filter */}
                  <div style={{ display:'flex', gap:'0.4rem', marginBottom:'1rem', flexWrap:'wrap' }}>
                    {['all',...STATUSES].map(s=>(
                      <button key={s} onClick={()=>setWaitlistFilter(s)} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:waitlistFilter===s?700:500, textTransform:'capitalize', padding:'0.3rem 0.8rem', borderRadius:100, border:`1px solid ${waitlistFilter===s?'#C4826A':'#E5E0D8'}`, background:waitlistFilter===s?'rgba(196,130,106,0.08)':'white', color:waitlistFilter===s?'#C4826A':'#6B6B6B', cursor:'pointer' }}>
                        {s}{s!=='all'?` (${counts[s]})`:` (${waitlist.length})`}
                      </button>
                    ))}
                  </div>

                  {rows.length===0 && <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#BCBCBC', textAlign:'center', padding:'2rem', fontSize:'0.875rem' }}>No leads here yet.</p>}

                  <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
                    {rows.map(l=>{
                      const tone = TONE[l.status] || TONE.new
                      return (
                        <div key={l.id} className="card" style={{ padding:'0.875rem 1rem' }}>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'0.75rem', flexWrap:'wrap' }}>
                            <div style={{ minWidth:0, flex:1 }}>
                              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.9rem', color:'#1A1A1A', margin:0 }}>
                                {l.name}{l.partner_name ? ` & ${l.partner_name}` : ''}
                                {l.tier === 'lifetime' && <span style={{ marginLeft:8, fontSize:'0.6rem', fontWeight:800, color:'#8A6D3B', background:'rgba(212,175,55,0.16)', border:'1px solid rgba(212,175,55,0.4)', borderRadius:100, padding:'2px 8px' }}>👑 Free for life</span>}
                                {l.tier === 'discount' && <span style={{ marginLeft:8, fontSize:'0.6rem', fontWeight:800, color:'#C4826A', background:'rgba(196,130,106,0.1)', border:'1px solid rgba(196,130,106,0.3)', borderRadius:100, padding:'2px 8px' }}>30% off</span>}
                                <span style={{ marginLeft:8, fontSize:'0.62rem', fontWeight:700, color:tone.c, background:tone.bg, borderRadius:100, padding:'2px 8px', textTransform:'capitalize' }}>{l.status}</span>
                              </p>
                              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.76rem', color:'#6B6B6B', margin:'0.3rem 0 0' }}>
                                <a href={`mailto:${l.email}`} style={{ color:'#C4826A', textDecoration:'none' }}>{l.email}</a>
                                {l.phone ? <> · <a href={`https://wa.me/${(l.phone||'').replace(/[^0-9]/g,'')}`} target="_blank" rel="noopener noreferrer" style={{ color:'#3D8B5E', textDecoration:'none' }}>{l.phone}</a></> : ''}
                              </p>
                              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:'0.25rem 0 0' }}>
                                {[l.location, l.country].filter(Boolean).join(', ') || '—'}
                                {l.wedding_date ? ` · 💍 ${fmtDate(l.wedding_date)}` : ''}
                                {l.guest_estimate ? ` · ${l.guest_estimate} guests` : ''}
                                {l.heard_from ? ` · via ${l.heard_from}` : ''}
                              </p>
                              {l.biggest_challenge && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.74rem', color:'#6B6B6B', fontStyle:'italic', margin:'0.35rem 0 0', lineHeight:1.5 }}>“{l.biggest_challenge}”</p>}
                            </div>
                            <div style={{ textAlign:'right', flexShrink:0 }}>
                              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.66rem', color:'#BCBCBC', margin:'0 0 0.4rem' }}>{fmtDate(l.created_at)}</p>
                              <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', justifyContent:'flex-end' }}>
                                <select value={l.status} onChange={e=>setLeadStatus(l.id, e.target.value)} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', padding:'0.3rem 0.5rem', border:'1px solid #E5E0D8', borderRadius:'0.5rem', color:'#1A1A1A', cursor:'pointer', textTransform:'capitalize' }}>
                                  {STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
                                </select>
                                <button onClick={()=>deleteLead(l.id)} title="Delete lead" style={{ background:'none', border:'none', cursor:'pointer', padding:4, color:'#BCBCBC', flexShrink:0 }}>
                                  <Trash2 size={15}/>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })()}

            {/* Email campaign — basic email marketing */}
            {tab==='email' && (
              <div>
                <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.2rem', margin:'0 0 0.2rem' }}>Email campaign</h3>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#9B9B9B', margin:'0 0 1.25rem', lineHeight:1.5 }}>Write an email, see exactly how it lands, then send it to your waitlist, your signed-up users, or everyone. Duplicate addresses are removed automatically.</p>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:'1.25rem', alignItems:'start' }}>

                  {/* Composer */}
                  <div className="card" style={{ padding:'1.25rem', display:'flex', flexDirection:'column', gap:'0.875rem' }}>
                    <div>
                      <label style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#6B6B6B', display:'block', margin:'0 0 0.3rem' }}>Subject</label>
                      <input value={campSubject} onChange={e=>setCampSubject(e.target.value)} placeholder="e.g. A little update from Okoiya"
                        style={{ width:'100%', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', border:'1px solid #E5E0D8', borderRadius:'0.75rem', padding:'0.6rem 0.875rem', outline:'none', boxSizing:'border-box' }}/>
                    </div>
                    <div>
                      <label style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#6B6B6B', display:'block', margin:'0 0 0.3rem' }}>Message</label>
                      <textarea value={campBody} onChange={e=>setCampBody(e.target.value)} rows={12}
                        placeholder={"Hi {{name}},\n\nWrite your message here...\n\nLeave a blank line to start a new paragraph."}
                        style={{ width:'100%', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', border:'1px solid #E5E0D8', borderRadius:'0.75rem', padding:'0.7rem 0.875rem', outline:'none', boxSizing:'border-box', resize:'vertical', lineHeight:1.6 }}/>
                      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#BCBCBC', margin:'0.35rem 0 0', lineHeight:1.6 }}>
                        Formatting: <strong>**bold**</strong> · <strong>[text](https://link)</strong> · blank line = new paragraph · <strong>{'{{name}}'}</strong> = recipient's first name.
                      </p>
                    </div>
                    <div>
                      <label style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#6B6B6B', display:'block', margin:'0 0 0.4rem' }}>Send to</label>
                      <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap' }}>
                        {[['all','Everyone'],['waitlist','Waitlist only'],['users','Signed-up users']].map(([v,l])=>(
                          <button key={v} onClick={()=>setCampAudience(v)} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.74rem', fontWeight:campAudience===v?700:500, padding:'0.4rem 0.85rem', borderRadius:100, border:`1px solid ${campAudience===v?'#C4826A':'#E5E0D8'}`, background:campAudience===v?'rgba(196,130,106,0.08)':'#fff', color:campAudience===v?'#C4826A':'#6B6B6B', cursor:'pointer' }}>{l}</button>
                        ))}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap', marginTop:'0.25rem' }}>
                      <button onClick={sendCampaignTest} disabled={campBusy} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', fontWeight:600, color:'#6B6B6B', background:'white', border:'1px solid #E5E0D8', borderRadius:100, padding:'0.55rem 1rem', cursor:campBusy?'default':'pointer', opacity:campBusy?0.6:1 }}>Send test to me</button>
                      <button onClick={sendCampaign} disabled={campBusy} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', fontWeight:700, color:'white', background:'#1A1A1A', border:'none', borderRadius:100, padding:'0.55rem 1.1rem', cursor:campBusy?'default':'pointer', opacity:campBusy?0.6:1, display:'inline-flex', alignItems:'center', gap:'0.35rem' }}>📣 Send campaign</button>
                    </div>
                    {campMsg && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:campMsg.startsWith('Done')||campMsg.includes('Test sent')?'#3D8B5E':'#6B6B6B', fontWeight:600, margin:0 }}>{campMsg}</p>}
                  </div>

                  {/* Live preview */}
                  <div>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#9B9B9B', textTransform:'uppercase', letterSpacing:'0.05em', margin:'0 0 0.5rem' }}>Live preview</p>
                    <div style={{ border:'1px solid #E5E0D8', borderRadius:'0.875rem', overflow:'hidden' }}>
                      <div style={{ background:'#fff', borderBottom:'1px solid #E5E0D8', padding:'0.6rem 0.875rem' }}>
                        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:0 }}>Subject</p>
                        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', fontWeight:700, color:'#1A1A1A', margin:'0.1rem 0 0' }}>{campSubject || '(no subject yet)'}</p>
                      </div>
                      <iframe title="Email preview" srcDoc={campComposeEmail(campBody).replaceAll('{{name}}','there')} style={{ width:'100%', height:520, border:'none', background:'#FAFAF8', display:'block' }}/>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Settings */}
            {tab==='settings' && (
              <div style={{ maxWidth:560, display:'flex', flexDirection:'column', gap:'1rem' }}>
                {/* Site live — master launch switch */}
                <div className="card" style={{ padding:'1.25rem', border:`1px solid ${siteLive?'#E5E0D8':'rgba(217,119,6,0.4)'}`, background:siteLive?'white':'rgba(217,119,6,0.04)' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem' }}>
                    <div>
                      <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', margin:'0 0 0.2rem' }}>
                        Site live {siteLive
                          ? <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', fontWeight:800, color:'#3D8B5E', background:'rgba(61,139,94,0.1)', borderRadius:100, padding:'2px 8px', marginLeft:6, verticalAlign:'middle' }}>LIVE</span>
                          : <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', fontWeight:800, color:'#B45309', background:'rgba(217,119,6,0.12)', borderRadius:100, padding:'2px 8px', marginLeft:6, verticalAlign:'middle' }}>PRE-LAUNCH</span>}
                      </h3>
                      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#9B9B9B', margin:0, lineHeight:1.5 }}>
                        When off, the whole site is sealed behind the waitlist for everyone except admins. Use it to launch — or to take the site down while you work.
                      </p>
                    </div>
                    <ToggleBtn on={siteLive} onClick={()=>{ const next=!siteLive; setSiteLive(next); requestAction({ action:'set_setting', key:'launch', value:{ live:next } }, next?'Take the site LIVE for everyone':'Seal the site behind the waitlist') }}/>
                  </div>
                </div>


                {/* Maintenance banner */}
                <div className="card" style={{ padding:'1.25rem' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem', marginBottom:'0.75rem' }}>
                    <div>
                      <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', margin:'0 0 0.2rem' }}>Maintenance banner</h3>
                      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#9B9B9B', margin:0 }}>Shows a banner across the whole site for everyone.</p>
                    </div>
                    <ToggleBtn on={maintEnabled} onClick={()=>setMaintEnabled(v=>!v)}/>
                  </div>
                  <textarea value={maintMsg} onChange={e=>setMaintMsg(e.target.value)} rows={2} placeholder="Banner message (optional)"
                    style={{ width:'100%', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', border:'1px solid #E5E0D8', borderRadius:'0.75rem', padding:'0.6rem 0.875rem', outline:'none', boxSizing:'border-box', resize:'vertical', marginBottom:'0.75rem' }}/>
                  <button onClick={()=>requestAction({ action:'set_setting', key:'maintenance', value:{ enabled:maintEnabled, message:maintMsg } }, 'Update the maintenance banner')}
                    style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.8rem', color:'white', background:'#C4826A', border:'none', borderRadius:100, padding:'0.6rem 1.25rem', cursor:'pointer' }}>
                    Save banner
                  </button>
                </div>

                {/* Pause signups */}
                <div className="card" style={{ padding:'1.25rem' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem' }}>
                    <div>
                      <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', margin:'0 0 0.2rem' }}>Pause new signups</h3>
                      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#9B9B9B', margin:0 }}>New visitors see a "signups paused" message instead of the form.</p>
                    </div>
                    <ToggleBtn on={signupsPaused} onClick={()=>{ const next=!signupsPaused; setSignupsPaused(next); requestAction({ action:'set_setting', key:'signups_paused', value:{ enabled:next } }, next?'Pause new signups':'Resume new signups') }}/>
                  </div>
                </div>

                {/* Two-factor authentication (TOTP) */}
                <div className="card" style={{ padding:'1.25rem' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem' }}>
                    <div>
                      <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', margin:'0 0 0.2rem', display:'flex', alignItems:'center', gap:'0.4rem' }}>
                        {my2fa ? <ShieldCheck size={17} color="#3D8B5E"/> : <Shield size={17} color="#BCBCBC"/>}
                        Two-factor authentication
                      </h3>
                      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#9B9B9B', margin:0 }}>
                        {my2fa
                          ? <span style={{ color:'#3D8B5E', fontWeight:700 }}>On — sensitive actions need a 6-digit code.</span>
                          : 'Protect your account with an authenticator app (Google Authenticator, Authy…).'}
                      </p>
                    </div>
                    {!enrollData && (
                      my2fa
                        ? <button onClick={disable2fa} disabled={enrollBusy} style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.78rem', color:'#C4826A', background:'white', border:'1.5px solid #E5E0D8', borderRadius:100, padding:'0.5rem 1rem', cursor:'pointer', flexShrink:0 }}>Disable</button>
                        : <button onClick={enroll2fa} disabled={enrollBusy} style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.78rem', color:'white', background:'#9BAF9B', border:'none', borderRadius:100, padding:'0.5rem 1rem', cursor:'pointer', flexShrink:0 }}>{enrollBusy?'…':'Enable 2FA'}</button>
                    )}
                  </div>

                  {/* Enrollment panel */}
                  {enrollData && (
                    <div style={{ marginTop:'1rem', borderTop:'1px solid #F0EDE7', paddingTop:'1rem' }}>
                      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#6B6B6B', margin:'0 0 0.75rem', lineHeight:1.6 }}>
                        1. Scan this with your authenticator app, then enter the 6-digit code it shows.
                      </p>
                      <div style={{ display:'flex', gap:'1rem', flexWrap:'wrap', alignItems:'center' }}>
                        <img src={enrollData.qr} alt="2FA QR code" style={{ width:140, height:140, border:'1px solid #E5E0D8', borderRadius:'0.75rem', background:'white' }}/>
                        <div style={{ flex:'1 1 180px' }}>
                          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#9B9B9B', margin:'0 0 0.25rem' }}>Or enter this key manually:</p>
                          <code style={{ fontFamily:'monospace', fontSize:'0.72rem', color:'#1A1A1A', background:'#F3F0EA', borderRadius:'0.5rem', padding:'0.4rem 0.6rem', display:'inline-block', wordBreak:'break-all', marginBottom:'0.75rem' }}>{enrollData.secret}</code>
                          <input value={enrollCode} onChange={e=>setEnrollCode(e.target.value.replace(/\D/g,'').slice(0,6))} placeholder="123456" inputMode="numeric"
                            style={{ width:'100%', fontFamily:'monospace', letterSpacing:'0.3em', fontSize:'1rem', textAlign:'center', border:`1.5px solid ${enrollErr?'#DC2626':'#E5E0D8'}`, borderRadius:'0.75rem', padding:'0.6rem', outline:'none', boxSizing:'border-box', marginBottom:'0.5rem' }}/>
                          <div style={{ display:'flex', gap:'0.5rem' }}>
                            <button onClick={()=>{ setEnrollData(null); setEnrollErr('') }} style={{ flex:1, fontFamily:'Nunito Sans, sans-serif', fontWeight:600, fontSize:'0.78rem', color:'#6B6B6B', background:'#F3F0EA', border:'none', borderRadius:100, padding:'0.55rem', cursor:'pointer' }}>Cancel</button>
                            <button onClick={verifyEnroll} disabled={enrollBusy} style={{ flex:1, fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.78rem', color:'white', background:enrollBusy?'#D4C4BC':'#9BAF9B', border:'none', borderRadius:100, padding:'0.55rem', cursor:'pointer' }}>{enrollBusy?'Verifying…':'Verify & enable'}</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {enrollErr && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#DC2626', margin:'0.75rem 0 0' }}>{enrollErr}</p>}
                </div>

                {/* Admin manager */}
                <div className="card" style={{ padding:'1.25rem' }}>
                  <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', margin:'0 0 0.2rem' }}>Admins</h3>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#9B9B9B', margin:'0 0 1rem' }}>
                    These accounts can open this dashboard. Adding or removing one needs your password{my2fa ? ' + 2FA code' : ''}.
                  </p>
                  <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem', marginBottom:'1rem' }}>
                    {admins.map(a=>{
                      const isSuper = superAdmins.includes(a.email)
                      const isMe = a.email === meEmail
                      return (
                        <div key={a.email} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.75rem', background:'#FAFAF8', border:'1px solid #F0EDE7', borderRadius:'0.625rem', padding:'0.5rem 0.75rem' }}>
                          <div style={{ minWidth:0 }}>
                            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.8rem', color:'#1A1A1A', margin:0, overflow:'hidden', textOverflow:'ellipsis' }}>
                              {a.email}{isMe && <span style={{ color:'#BCBCBC', fontWeight:400 }}> (you)</span>}
                            </p>
                            <div style={{ display:'flex', gap:'0.35rem', marginTop:'0.15rem', flexWrap:'wrap' }}>
                              {a.name && <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.65rem', color:'#9B9B9B' }}>{a.name}</span>}
                              {isSuper && <span style={{ fontSize:'0.6rem', fontWeight:700, color:'#C4826A', background:'rgba(196,130,106,0.1)', borderRadius:100, padding:'1px 7px' }}>OWNER</span>}
                              {a.mfa_enabled && <span style={{ fontSize:'0.6rem', fontWeight:700, color:'#3D8B5E', background:'rgba(61,139,94,0.1)', borderRadius:100, padding:'1px 7px' }}>2FA</span>}
                            </div>
                          </div>
                          {!isSuper && (
                            <button onClick={()=>requestAction({ action:'remove_admin', email:a.email }, `Remove ${a.email} from admins`)} title="Remove admin" style={{ background:'none', border:'none', cursor:'pointer', padding:4, color:'#BCBCBC', flexShrink:0 }}>
                              <Trash2 size={15}/>
                            </button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                    <input value={addAdminEmail} onChange={e=>setAddAdminEmail(e.target.value)} type="email" placeholder="newadmin@email.com"
                      style={{ flex:'1 1 200px', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', border:'1px solid #E5E0D8', borderRadius:100, padding:'0.6rem 1rem', outline:'none' }}/>
                    <input value={addAdminName} onChange={e=>setAddAdminName(e.target.value)} placeholder="Name (optional)"
                      style={{ flex:'1 1 130px', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', border:'1px solid #E5E0D8', borderRadius:100, padding:'0.6rem 1rem', outline:'none' }}/>
                    <button onClick={submitAddAdmin} style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem', color:'white', background:'#C4826A', border:'none', borderRadius:100, padding:'0.6rem 1.25rem', cursor:'pointer', display:'flex', alignItems:'center', gap:'0.4rem', flexShrink:0 }}>
                      <UserPlus size={14}/> Add
                    </button>
                  </div>
                </div>

                {actionMsg && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#3D8B5E', margin:0, fontWeight:600 }}>{actionMsg}</p>}
              </div>
            )}

            {/* Users */}
            {tab==='users' && (() => {
              const q = userSearch.trim().toLowerCase()
              const rows = users.filter(u => {
                if (userPlanFilter==='premium' && u.plan!=='premium') return false
                if (userPlanFilter==='free'    && u.plan==='premium') return false
                if (q && !`${u.full_name||''} ${u.email||''}`.toLowerCase().includes(q)) return false
                return true
              })
              const premCount = users.filter(u=>u.plan==='premium').length
              return (
                <div>
                  {/* Toolbar */}
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.75rem', flexWrap:'wrap', marginBottom:'1rem' }}>
                    <input value={userSearch} onChange={e=>setUserSearch(e.target.value)} placeholder="Search name or email…"
                      style={{ flex:'1 1 240px', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', border:'1px solid #E5E0D8', borderRadius:100, padding:'0.55rem 1rem', outline:'none', background:'#fff' }}/>
                    <div style={{ display:'flex', gap:'0.35rem' }}>
                      {[['all','All',users.length],['premium','Premium',premCount],['free','Free',users.length-premCount]].map(([v,l,n])=>(
                        <button key={v} onClick={()=>setUserPlanFilter(v)} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.74rem', fontWeight:userPlanFilter===v?700:600, padding:'0.4rem 0.85rem', borderRadius:100, border:`1px solid ${userPlanFilter===v?'#C4826A':'#E5E0D8'}`, background:userPlanFilter===v?'rgba(196,130,106,0.08)':'#fff', color:userPlanFilter===v?'#C4826A':'#6B6B6B', cursor:'pointer', whiteSpace:'nowrap' }}>{l} ({n})</button>
                      ))}
                    </div>
                  </div>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:'0 0 0.75rem' }}>Showing {rows.length} of {users.length} users</p>

                  <div className="card" style={{ padding:'0.25rem 0.5rem', overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem' }}>
                      <thead>
                        <tr style={{ borderBottom:'1px solid #ECECEF', textAlign:'left' }}>
                          <th style={{ padding:'0.65rem 0.5rem', color:'#9B9B9B', fontWeight:700, fontSize:'0.68rem', textTransform:'uppercase', letterSpacing:'0.05em' }}>User</th>
                          <th style={{ padding:'0.65rem 0.5rem', color:'#9B9B9B', fontWeight:700, fontSize:'0.68rem', textTransform:'uppercase', letterSpacing:'0.05em' }}>Plan</th>
                          <th style={{ padding:'0.65rem 0.5rem', color:'#9B9B9B', fontWeight:700, fontSize:'0.68rem', textTransform:'uppercase', letterSpacing:'0.05em' }}>Joined</th>
                          <th style={{ padding:'0.65rem 0.5rem' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length===0 && <tr><td colSpan={4} style={{ padding:'2rem', textAlign:'center', color:'#BCBCBC' }}>No users match.</td></tr>}
                        {rows.map(u=>{
                          const isPrem = u.plan==='premium'
                          const initial = (u.full_name||u.email||'?').trim().charAt(0).toUpperCase()
                          return (
                          <tr key={u.id} style={{ borderBottom:'1px solid #F6F4F1' }}>
                            <td style={{ padding:'0.6rem 0.5rem' }}>
                              <div style={{ display:'flex', alignItems:'center', gap:'0.6rem' }}>
                                <div style={{ width:32, height:32, borderRadius:'50%', background:isPrem?'rgba(155,175,155,0.18)':'#F3F0EA', color:isPrem?'#7A917A':'#9B9B9B', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:'0.82rem', flexShrink:0 }}>{initial}</div>
                                <div style={{ minWidth:0 }}>
                                  <p style={{ margin:0, fontWeight:700, color:'#1A1A1A' }}>{u.full_name || '—'}</p>
                                  <p style={{ margin:0, fontSize:'0.72rem', color:'#9B9B9B', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:240 }}>{u.email || '—'}</p>
                                </div>
                              </div>
                            </td>
                            <td style={{ padding:'0.6rem 0.5rem' }}>
                              <span style={{ fontSize:'0.66rem', fontWeight:700, textTransform:'capitalize', color:isPrem?'#3D8B5E':'#9B9B9B', background:isPrem?'rgba(61,139,94,0.1)':'#F3F0EA', borderRadius:100, padding:'2px 9px' }}>{u.plan||'free'}</span>
                            </td>
                            <td style={{ padding:'0.6rem 0.5rem', color:'#6B6B6B', whiteSpace:'nowrap' }}>{fmtDate(u.created_at)}</td>
                            <td style={{ padding:'0.6rem 0.5rem', textAlign:'right' }}>
                              {u.email && (
                                <button onClick={()=>requestAction({ action:'set_premium', email:u.email, value:!isPrem }, `${isPrem?'Revoke premium from':'Grant premium to'} ${u.email}`)}
                                  style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.68rem', whiteSpace:'nowrap', color:isPrem?'#C4826A':'#3D8B5E', background:'white', border:`1.5px solid ${isPrem?'rgba(196,130,106,0.4)':'rgba(61,139,94,0.35)'}`, borderRadius:100, padding:'0.3rem 0.7rem', cursor:'pointer' }}>
                                  {isPrem?'Revoke':'Make Premium'}
                                </button>
                              )}
                            </td>
                          </tr>
                        )})}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            })()}

            {/* Connections — couples where two accounts are linked */}
            {tab==='connections' && (
              <div>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#9B9B9B', margin:'0 0 1rem', lineHeight:1.5 }}>
                  Weddings where a second account accepted the partner invite — the two emails below share co-admin access. Couples who only typed both names (no second account) aren't listed.
                </p>
                {linkedCouples.length === 0 ? (
                  <p style={{ color:'#BCBCBC', textAlign:'center', padding:'2.5rem', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem' }}>No connected couples yet — no one has accepted a partner invite.</p>
                ) : (
                  <>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9BAF9B', fontWeight:700, margin:'0 0 0.75rem' }}>{linkedCouples.length} connected couple{linkedCouples.length!==1?'s':''}</p>
                    <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                      {linkedCouples.map(c=>(
                        <div key={c.id} className="card" style={{ padding:'0.875rem 1rem' }}>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:'0.75rem', marginBottom:'0.6rem' }}>
                            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', margin:0 }}>
                              {c.partner1_name} & {c.partner2_name}
                              <span style={{ marginLeft:6, fontSize:'0.6rem', fontWeight:700, color:'#9BAF9B', background:'rgba(155,175,155,0.12)', borderRadius:100, padding:'1px 7px' }}>🔗 linked</span>
                            </p>
                            <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.62rem', fontWeight:700, color:c.plan==='premium'?'#9BAF9B':'#BCBCBC', background:c.plan==='premium'?'rgba(155,175,155,0.1)':'#F3F0EA', borderRadius:100, padding:'2px 8px', flexShrink:0 }}>{c.plan}</span>
                          </div>
                          <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap' }}>
                            <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.74rem', color:'#1A1A1A', background:'#FAFAF8', border:'1px solid #F0EDE7', borderRadius:100, padding:'0.25rem 0.7rem' }}>{c.partner1Email || '—'}</span>
                            <span style={{ color:'#C4826A', fontWeight:700 }}>✚</span>
                            <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.74rem', color:'#1A1A1A', background:'#FAFAF8', border:'1px solid #F0EDE7', borderRadius:100, padding:'0.25rem 0.7rem' }}>{c.partner2Email || '—'}</span>
                          </div>
                          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.66rem', color:'#BCBCBC', margin:'0.5rem 0 0' }}>/w/{c.slug} · linked {fmtDate(c.created_at)}</p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Vendors — Aisle application review */}
            {tab==='vendors' && (
              <div>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#9B9B9B', margin:'0 0 1rem', lineHeight:1.5 }}>
                  Pending Okoiya Aisle vendor applications. Review the portfolio, documents and references, then approve (grants the Verified badge + lists them) or reject.
                </p>
                {pendingVendors.length === 0 ? (
                  <p style={{ color:'#BCBCBC', textAlign:'center', padding:'2.5rem', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem' }}>No pending applications.</p>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
                    {pendingVendors.map(v=>(
                      <div key={v.id} className="card" style={{ padding:'1.25rem' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'0.75rem', flexWrap:'wrap' }}>
                          <div>
                            <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.15rem', margin:'0 0 0.15rem' }}>{v.business_name}</h3>
                            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#9B9B9B', margin:0 }}>
                              {v.category} · {v.city || '—'} · from ₦{Number(v.starting_price||0).toLocaleString()}
                            </p>
                          </div>
                          <span style={{ fontSize:'0.62rem', fontWeight:700, color:'#9B9B9B', background:'#F3F0EA', borderRadius:100, padding:'2px 8px' }}>PENDING</span>
                        </div>

                        {v.vendor_vow && <p style={{ fontFamily:'Gibs, serif', fontStyle:'italic', fontSize:'0.95rem', color:'#1A1A1A', lineHeight:1.6, margin:'0.75rem 0' }}>“{v.vendor_vow}”</p>}
                        {v.description && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#6B6B6B', lineHeight:1.6, margin:'0 0 0.75rem' }}>{v.description}</p>}

                        {/* Gallery thumbnails */}
                        {Array.isArray(v.gallery_images) && v.gallery_images.length > 0 && (
                          <div style={{ display:'flex', gap:'0.5rem', overflowX:'auto', marginBottom:'0.75rem' }}>
                            {v.gallery_images.map((src,i)=>(
                              <img key={i} src={src} alt="" style={{ width:90, height:110, objectFit:'cover', borderRadius:'0.5rem', flexShrink:0, background:'#F3F0EA' }}/>
                            ))}
                          </div>
                        )}

                        {/* Contact + references + docs */}
                        <div style={{ display:'flex', flexWrap:'wrap', gap:'1.25rem', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.76rem', color:'#6B6B6B', marginBottom:'0.875rem' }}>
                          <div>
                            <p style={{ fontWeight:700, color:'#1A1A1A', margin:'0 0 0.2rem' }}>Contact</p>
                            <p style={{ margin:0 }}>{v.contact_email || '—'}</p>
                            <p style={{ margin:0 }}>{v.contact_phone || '—'}</p>
                          </div>
                          {Array.isArray(v.references_list) && v.references_list.length > 0 && (
                            <div>
                              <p style={{ fontWeight:700, color:'#1A1A1A', margin:'0 0 0.2rem' }}>References</p>
                              {v.references_list.map((r,i)=>(<p key={i} style={{ margin:0 }}>{r.name} · {r.contact} {r.relationship?`(${r.relationship})`:''}</p>))}
                            </div>
                          )}
                          {Array.isArray(v.document_urls) && v.document_urls.length > 0 && (
                            <div>
                              <p style={{ fontWeight:700, color:'#1A1A1A', margin:'0 0 0.2rem' }}>Documents</p>
                              {v.document_urls.map((u,i)=>(<a key={i} href={u} target="_blank" rel="noreferrer" style={{ display:'block', color:'#C4826A' }}>View document {i+1}</a>))}
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        {rejectingId === v.id ? (
                          <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap', alignItems:'center' }}>
                            <input value={rejectReason} onChange={e=>setRejectReason(e.target.value)} placeholder="Reason (optional)"
                              style={{ flex:'1 1 200px', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', border:'1px solid #E5E0D8', borderRadius:100, padding:'0.5rem 0.9rem', outline:'none' }}/>
                            <button onClick={()=>reviewVendor(v.id, 'rejected', rejectReason)} style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.78rem', color:'white', background:'#DC2626', border:'none', borderRadius:100, padding:'0.5rem 1rem', cursor:'pointer' }}>Confirm reject</button>
                            <button onClick={()=>{ setRejectingId(null); setRejectReason('') }} style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:600, fontSize:'0.78rem', color:'#6B6B6B', background:'#F3F0EA', border:'none', borderRadius:100, padding:'0.5rem 1rem', cursor:'pointer' }}>Cancel</button>
                          </div>
                        ) : (
                          <div style={{ display:'flex', gap:'0.5rem' }}>
                            <button onClick={()=>reviewVendor(v.id, 'verified')} style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.8rem', color:'white', background:'#9BAF9B', border:'none', borderRadius:100, padding:'0.55rem 1.25rem', cursor:'pointer' }}>Approve</button>
                            <button onClick={()=>{ setRejectingId(v.id); setRejectReason('') }} style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.8rem', color:'#DC2626', background:'white', border:'1.5px solid rgba(220,38,38,0.3)', borderRadius:100, padding:'0.55rem 1.25rem', cursor:'pointer' }}>Reject</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Payments */}
            {tab==='payments' && (() => {
              const view     = revView || stats.revenueMode || 'test'
              const streams  = stats.revenueStreams?.[view] || { premium:{}, gift:{}, asoebi:{} }
              const counts   = stats.revenueCounts?.[view]  || { premium:0, gift:0, asoebi:0 }
              const gmv      = stats.revenueGMV?.[view]     || { gift:0, asoebi:0 }
              const daily    = (stats.revenueDaily?.[view]  || []).slice(-chartDays)
              const premiumNGN = streams.premium?.NGN || 0
              const giftNGN    = streams.gift?.NGN || 0
              const asoNGN     = streams.asoebi?.NGN || 0
              const commNGN    = giftNGN + asoNGN
              const netNGN     = premiumNGN + commNGN
              const gmvNGN     = (gmv.gift || 0) + (gmv.asoebi || 0)
              const txCount    = (counts.premium||0) + (counts.gift||0) + (counts.asoebi||0)
              const avgNGN     = txCount ? Math.round(netNGN / txCount) : 0
              const segCurrencies = [...new Set([...Object.keys(streams.premium||{}), ...Object.keys(streams.gift||{}), ...Object.keys(streams.asoebi||{})])].sort()
              const chartData  = daily.map(d => ({ label: fmtDate(d.day).replace(/ \d{4}$/,''), premium: d.premium||0, commission: (d.gift||0)+(d.asoebi||0) }))
              const rangeTotal = chartData.reduce((s,d)=> s + d.premium + d.commission, 0)
              const byProvider = payments.reduce((a,p)=>{ const k=(p.provider||'other').toLowerCase(); a[k]=(a[k]||0)+1; return a }, {})
              const provRows   = Object.entries(byProvider).sort((a,b)=>b[1]-a[1])
              const provMax    = Math.max(1, ...provRows.map(r=>r[1]))
              const TXMETA = { premium:{ label:'Premium', color:'#C9A84C' }, gift:{ label:'Cash gift', color:'#C4826A' }, asoebi:{ label:'Aso Ebi', color:'#9BAF9B' } }
              const ledger = recentTx.filter(t => t.mode===view && (txFilter==='all' || t.type===txFilter))
              const monthly  = stats.revenueMonthly?.[view] || []
              const monthNet = monthly.map(m => ({ label: new Date(m.month + '-01').toLocaleDateString('en-GB', { month:'short' }), value: (m.premium||0)+(m.gift||0)+(m.asoebi||0) }))
              const thisM = monthNet.length ? monthNet[monthNet.length-1].value : 0
              const lastM = monthNet.length>1 ? monthNet[monthNet.length-2].value : 0
              const momPct = lastM>0 ? Math.round(((thisM-lastM)/lastM)*100) : (thisM>0 ? 100 : 0)
              const exportLedgerCSV = () => {
                const rows = [['Type','Couple','Amount','Currency','Your revenue','Mode','Date']]
                ledger.forEach(t => rows.push([(({premium:'Premium',gift:'Cash gift',asoebi:'Aso Ebi'})[t.type] || t.type), t.couple || '', t.gross, t.currency, t.fee, t.mode, new Date(t.date).toISOString().slice(0,10)]))
                const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n')
                const a = document.createElement('a'); a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv); a.download = `okoiya-ledger-${view}-${new Date().toISOString().slice(0,10)}.csv`; document.body.appendChild(a); a.click(); document.body.removeChild(a)
              }
              const toggle = (active) => ({ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:active?700:500, padding:'0.3rem 0.875rem', borderRadius:100, border:'none', cursor:'pointer', background:active?'white':'transparent', color:active?'#1A1A1A':'#9B9B9B' })
              const KPI = ({ label, value, sub, accent }) => (
                <div style={{ background:'#fff', border:'1px solid #ECECEF', borderRadius:'1rem', padding:'1rem 1.125rem' }}>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.62rem', color:'#9B9B9B', fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase', margin:'0 0 0.4rem' }}>{label}</p>
                  <p style={{ fontFamily:'Gibs, serif', fontSize:'1.45rem', color:accent||'#1A1A1A', margin:0, fontWeight:400, lineHeight:1 }}>{value}</p>
                  {sub && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.66rem', color:'#9B9B9B', margin:'0.35rem 0 0', lineHeight:1.4 }}>{sub}</p>}
                </div>
              )
              return (
                <div>
                  {/* Header + mode toggle */}
                  <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'0.75rem', flexWrap:'wrap', marginBottom:'1.1rem' }}>
                    <div>
                      <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.25rem', margin:0 }}>Revenue &amp; accounts</h3>
                      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.74rem', color:'#9B9B9B', margin:'0.15rem 0 0' }}>
                        Showing <strong style={{ color: view==='real'?'#3D8B5E':'#C4826A' }}>{view==='real'?'live money':'test data'}</strong> · headline figures in ₦
                      </p>
                    </div>
                    <div style={{ display:'flex', gap:'0.3rem', background:'#F3F0EA', borderRadius:100, padding:'2px' }}>
                      {['test','real'].map(m=>(<button key={m} onClick={()=>setRevView(m)} style={toggle(view===m)}>{m==='real'?'Live money':'Test'}</button>))}
                    </div>
                  </div>

                  {/* KPI cards */}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:'0.75rem', marginBottom:'1.25rem' }}>
                    <KPI label="Net revenue" value={fmt(netNGN)} sub={`${txCount} transaction${txCount===1?'':'s'}`} accent="#3D8B5E"/>
                    <KPI label="Premium" value={fmt(premiumNGN)} sub={`${counts.premium||0} upgrade${counts.premium===1?'':'s'}`}/>
                    <KPI label="Commissions" value={fmt(commNGN)} sub={`gifts ${fmt(giftNGN)} · aso ebi ${fmt(asoNGN)}`}/>
                    <KPI label="Raised for couples" value={fmt(gmvNGN)} sub="gross gifts + aso ebi"/>
                    <KPI label="Avg / transaction" value={fmt(avgNGN)} sub="your take per txn"/>
                  </div>

                  {/* Daily revenue chart */}
                  <div className="card" style={{ padding:'1.25rem', marginBottom:'1.25rem' }}>
                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'0.5rem', marginBottom:'0.875rem' }}>
                      <div>
                        <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', margin:0 }}>Daily revenue</h3>
                        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:'0.1rem 0 0' }}><strong style={{ color:'#3D8B5E' }}>{fmt(rangeTotal)}</strong> in the last {chartDays} days (₦ only)</p>
                      </div>
                      <div style={{ display:'flex', gap:'0.3rem', background:'#F3F0EA', borderRadius:100, padding:'2px' }}>
                        {[30,60].map(n=>(<button key={n} onClick={()=>setChartDays(n)} style={toggle(chartDays===n)}>{n}d</button>))}
                      </div>
                    </div>
                    {rangeTotal===0
                      ? <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#BCBCBC', margin:'1rem 0', textAlign:'center' }}>No {view==='real'?'live':'test'} revenue in this period yet.</p>
                      : <StackedRevenueChart data={chartData} height={270}/>}
                  </div>

                  {/* Monthly trend + month-over-month */}
                  <div className="card" style={{ padding:'1.25rem', marginBottom:'1.25rem' }}>
                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'0.5rem', marginBottom:'0.875rem' }}>
                      <div>
                        <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', margin:0 }}>Monthly trend</h3>
                        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:'0.1rem 0 0' }}>Net revenue, last 6 months (₦ only)</p>
                      </div>
                      <div style={{ textAlign:'right' }}>
                        <p style={{ fontFamily:'Gibs, serif', fontSize:'1.3rem', color:'#1A1A1A', margin:0, lineHeight:1 }}>{fmt(thisM)}</p>
                        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', margin:'0.25rem 0 0', color: momPct>=0?'#3D8B5E':'#DC2626', fontWeight:700 }}>
                          {momPct>=0?'▲':'▼'} {Math.abs(momPct)}% <span style={{ color:'#9B9B9B', fontWeight:500 }}>vs last month</span>
                        </p>
                      </div>
                    </div>
                    {thisM===0 && lastM===0
                      ? <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#BCBCBC', margin:'1rem 0', textAlign:'center' }}>No {view==='real'?'live':'test'} revenue in the last 6 months yet.</p>
                      : <AxisBarChart data={monthNet} color="#3D8B5E" height={200} yTitle="Revenue (₦)" xTitle="Month"/>}
                  </div>

                  {/* By revenue stream */}
                  <div className="card" style={{ padding:'1.25rem', marginBottom:'1.25rem' }}>
                    <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', margin:'0 0 0.875rem' }}>By revenue stream</h3>
                    <div style={{ display:'flex', flexDirection:'column', gap:'0.875rem' }}>
                      {[['premium','Premium upgrades',premiumNGN,counts.premium],['gift','Cash gifts',giftNGN,counts.gift],['asoebi','Aso Ebi',asoNGN,counts.asoebi]].map(([key,label,amt,n])=>{
                        const pct = netNGN ? Math.round((amt/netNGN)*100) : 0
                        return (
                          <div key={key}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:'0.3rem', gap:'0.5rem', flexWrap:'wrap' }}>
                              <span style={{ display:'inline-flex', alignItems:'center', gap:'0.45rem', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem', color:'#1A1A1A' }}>
                                <span style={{ width:9, height:9, borderRadius:3, background:TXMETA[key].color }}/>{label}
                                <span style={{ fontWeight:500, color:'#BCBCBC', fontSize:'0.72rem' }}>· {n||0}</span>
                              </span>
                              <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#1A1A1A', fontWeight:700 }}>{fmt(amt)} <span style={{ color:'#BCBCBC', fontWeight:500, fontSize:'0.72rem' }}>{pct}%</span></span>
                            </div>
                            <div style={{ height:7, background:'#F3F0EA', borderRadius:100, overflow:'hidden' }}>
                              <div style={{ height:'100%', width:`${pct}%`, background:TXMETA[key].color, borderRadius:100, transition:'width 0.4s' }}/>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* By currency + by provider */}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:'1.25rem', marginBottom:'1.25rem' }}>
                    <div className="card" style={{ padding:'1.25rem' }}>
                      <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', margin:'0 0 0.875rem' }}>By currency</h3>
                      {segCurrencies.length===0 ? (
                        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#BCBCBC', margin:0 }}>No {view==='real'?'live':'test'} revenue yet.</p>
                      ) : (
                        <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
                          {segCurrencies.map(cur=>{
                            const prem=streams.premium?.[cur]||0, gft=streams.gift?.[cur]||0, aso=streams.asoebi?.[cur]||0
                            return (
                              <div key={cur} style={{ border:'1px solid #ECECEF', borderRadius:'0.875rem', padding:'0.75rem 0.875rem' }}>
                                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.64rem', color:'#9B9B9B', fontWeight:700, letterSpacing:'0.05em', margin:'0 0 0.4rem' }}>{cur}</p>
                                {[['Premium',prem],['Gifts',gft],['Aso Ebi',aso]].map(([l,v])=>(
                                  <div key={l} style={{ display:'flex', justifyContent:'space-between', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.76rem', color:'#6B6B6B', marginBottom:'0.15rem' }}><span>{l}</span><span style={{ fontWeight:700, color:'#1A1A1A' }}>{fmtCur(v, cur)}</span></div>
                                ))}
                                <div style={{ borderTop:'1px solid #F0EBE3', paddingTop:'0.3rem', marginTop:'0.2rem', display:'flex', justifyContent:'space-between', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', fontWeight:800, color:'#3D8B5E' }}><span>Total</span><span>{fmtCur(prem+gft+aso, cur)}</span></div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                    <div className="card" style={{ padding:'1.25rem' }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.875rem' }}>
                        <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', margin:0 }}>Premium by provider</h3>
                        <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B' }}>{payments.length} total</span>
                      </div>
                      {provRows.length===0 ? (
                        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#BCBCBC', margin:0 }}>No verified premium payments yet.</p>
                      ) : (
                        <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
                          {provRows.map(([prov,n])=>{
                            const pct = Math.round((n/provMax)*100)
                            return (
                              <div key={prov}>
                                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'0.25rem' }}>
                                  <span style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.8rem', color:'#1A1A1A', textTransform:'capitalize' }}>{prov}</span>
                                  <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#C4826A', fontWeight:700 }}>{n}</span>
                                </div>
                                <div style={{ height:6, background:'#F3F0EA', borderRadius:100, overflow:'hidden' }}>
                                  <div style={{ height:'100%', width:`${pct}%`, background:'#C4826A', borderRadius:100, transition:'width 0.4s' }}/>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Transaction ledger */}
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'0.5rem', marginBottom:'0.65rem' }}>
                    <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', margin:0 }}>Transaction ledger <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', fontWeight:600 }}>({ledger.length})</span></h3>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap' }}>
                      <div style={{ display:'flex', gap:'0.3rem', background:'#F3F0EA', borderRadius:100, padding:'2px', flexWrap:'wrap' }}>
                        {[['all','All'],['premium','Premium'],['gift','Gifts'],['asoebi','Aso Ebi']].map(([k,l])=>(<button key={k} onClick={()=>setTxFilter(k)} style={toggle(txFilter===k)}>{l}</button>))}
                      </div>
                      <button onClick={exportLedgerCSV} disabled={ledger.length===0} title="Export this view to CSV"
                        style={{ display:'inline-flex', alignItems:'center', gap:'0.35rem', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.72rem', color: ledger.length?'#3D8B5E':'#BCBCBC', background:'#fff', border:'1px solid #E5E0D8', borderRadius:100, padding:'0.35rem 0.85rem', cursor: ledger.length?'pointer':'default' }}>
                        <Download size={13}/> CSV
                      </button>
                    </div>
                  </div>
                  <div className="card" style={{ padding:'0.25rem 0.5rem', overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem' }}>
                      <thead>
                        <tr style={{ borderBottom:'1px solid #ECECEF', textAlign:'left' }}>
                          {['Type','Couple','Amount','Your revenue','Date'].map(h=>(
                            <th key={h} style={{ padding:'0.65rem 0.5rem', color:'#9B9B9B', fontWeight:700, fontSize:'0.66rem', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {ledger.length===0 && <tr><td colSpan={5} style={{ padding:'2rem', textAlign:'center', color:'#BCBCBC' }}>No {view==='real'?'live':'test'} transactions yet.</td></tr>}
                        {ledger.slice(0,60).map((t,i)=>{
                          const meta = TXMETA[t.type] || { label:t.type, color:'#BCBCBC' }
                          return (
                            <tr key={i} style={{ borderBottom:'1px solid #F6F4F1' }}>
                              <td style={{ padding:'0.6rem 0.5rem' }}><span style={{ display:'inline-block', fontSize:'0.66rem', fontWeight:700, color:meta.color, background:`${meta.color}1A`, borderRadius:100, padding:'2px 9px', whiteSpace:'nowrap' }}>{meta.label}</span></td>
                              <td style={{ padding:'0.6rem 0.5rem', fontWeight:600, color:'#1A1A1A' }}>{t.couple || '—'}</td>
                              <td style={{ padding:'0.6rem 0.5rem', color:'#1A1A1A', whiteSpace:'nowrap' }}>{fmtCur(t.gross, t.currency)}</td>
                              <td style={{ padding:'0.6rem 0.5rem', fontWeight:700, color:'#3D8B5E', whiteSpace:'nowrap' }}>{fmtCur(t.fee, t.currency)}</td>
                              <td style={{ padding:'0.6rem 0.5rem', color:'#6B6B6B', whiteSpace:'nowrap' }}>{fmtDate(t.date)}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#BCBCBC', margin:'0.5rem 0 1.5rem', lineHeight:1.5 }}>
                    <strong>Amount</strong> = money the guest paid (gifts/aso ebi settle to the couple). <strong>Your revenue</strong> = Okoiya's cut (the fee; for premium it's the full upgrade).
                  </p>

                  {/* Payment settings (tucked away) */}
                  <div className="card" style={{ padding:'1rem 1.25rem' }}>
                    <button onClick={()=>setShowPaySettings(s=>!s)} style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', background:'none', border:'none', cursor:'pointer', padding:0 }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:'0.5rem', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', color:'#1A1A1A' }}><Settings size={15} color="#9B9B9B"/> Payment settings</span>
                      <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#9B9B9B' }}>{showPaySettings?'Hide ▴':'Show ▾'}</span>
                    </button>
                    {showPaySettings && (
                      <div style={{ marginTop:'1rem', borderTop:'1px solid #F0EBE3', paddingTop:'1rem' }}>
                        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:700, color:'#1A1A1A', margin:'0 0 0.25rem' }}>Live keys switched on</p>
                        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:'0 0 0.6rem', lineHeight:1.5 }}>
                          The cutoff that separates real money from test data. Everything on/after this moment counts as <strong>live money</strong>. (Per-transaction live flags already override this where present.)
                        </p>
                        <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap', alignItems:'center' }}>
                          <input type="datetime-local" value={liveAtInput || (stats.paymentsLiveAt ? new Date(new Date(stats.paymentsLiveAt).getTime() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,16) : '')} onChange={e=>setLiveAtInput(e.target.value)}
                            style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', border:'1px solid #E5E0D8', borderRadius:'0.6rem', padding:'0.45rem 0.7rem', outline:'none' }}/>
                          <button onClick={()=>requestAction({ action:'set_setting', key:'payments_live_at', value: liveAtInput ? new Date(liveAtInput).toISOString() : null }, 'Set the go-live cutoff for real revenue')}
                            style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.74rem', color:'white', background:'#C4826A', border:'none', borderRadius:100, padding:'0.45rem 1rem', cursor:'pointer' }}>Save</button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })()}

            {/* Actions */}
            {tab==='actions' && (
              <div style={{ maxWidth:540 }}>
                <div className="card" style={{ padding:'1.25rem' }}>
                  <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.1rem', margin:'0 0 0.35rem' }}>Premium access</h3>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#6B6B6B', margin:'0 0 1rem', lineHeight:1.6 }}>
                    Grant or revoke premium for a couple by their account email. You'll confirm with your admin password.
                  </p>
                  <input value={actionEmail} onChange={e=>setActionEmail(e.target.value)} type="email" placeholder="couple@email.com"
                    style={{ width:'100%', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', border:'1px solid #E5E0D8', borderRadius:'0.75rem', padding:'0.7rem 1rem', outline:'none', boxSizing:'border-box', marginBottom:'0.75rem' }}/>
                  <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                    <button onClick={()=>{ if(actionEmail.trim()) requestAction({ action:'set_premium', email:actionEmail.trim(), value:true }, `Grant premium to ${actionEmail.trim()}`) }}
                      style={{ flex:'1 1 140px', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem', color:'white', background:'#9BAF9B', border:'none', borderRadius:100, padding:'0.7rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.4rem' }}>
                      <Crown size={14}/> Grant Premium
                    </button>
                    <button onClick={()=>{ if(actionEmail.trim()) requestAction({ action:'set_premium', email:actionEmail.trim(), value:false }, `Revoke premium from ${actionEmail.trim()}`) }}
                      style={{ flex:'1 1 120px', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem', color:'#C4826A', background:'white', border:'1.5px solid #E5E0D8', borderRadius:100, padding:'0.7rem', cursor:'pointer' }}>
                      Revoke Premium
                    </button>
                  </div>
                  {actionMsg && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#3D8B5E', margin:'0.875rem 0 0', fontWeight:600 }}>{actionMsg}</p>}
                </div>
              </div>
            )}

            {/* Photo moderation */}
            {tab==='photos' && (
              <div>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#9B9B9B', margin:'0 0 1rem', lineHeight:1.5 }}>
                  Recent guest photos from public portals. Hide anything inappropriate — it disappears from the couple's portal immediately.
                </p>
                {guestPhotos.length === 0 ? (
                  <p style={{ color:'#BCBCBC', textAlign:'center', padding:'2rem' }}>No guest photos yet</p>
                ) : (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(150px, 1fr))', gap:'0.75rem' }}>
                    {guestPhotos.map(p=>(
                      <div key={p.id} style={{ border:'1px solid #E5E0D8', borderRadius:'0.75rem', overflow:'hidden', background:'white' }}>
                        <div style={{ position:'relative', background:'#F3F0EA' }}>
                          <img src={p.public_url} alt="" loading="lazy" style={{ width:'100%', aspectRatio:'1', objectFit:'cover', display:'block', opacity:p.hidden?0.3:1 }}/>
                          {p.hidden && <span style={{ position:'absolute', top:6, left:6, fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', fontWeight:700, color:'white', background:'#DC2626', borderRadius:100, padding:'2px 8px' }}>HIDDEN</span>}
                        </div>
                        <div style={{ padding:'0.5rem 0.625rem' }}>
                          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#1A1A1A', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.weddings ? `${p.weddings.partner1_name} & ${p.weddings.partner2_name}` : '—'}</p>
                          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.64rem', color:'#BCBCBC', margin:'0 0 0.4rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.uploader_name || 'Guest'} · {fmtDate(p.created_at)}</p>
                          <button onClick={()=>moderatePhoto(p.id, !p.hidden)} style={{ width:'100%', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.7rem', color:p.hidden?'#3D8B5E':'#DC2626', background:'white', border:`1.5px solid ${p.hidden?'rgba(61,139,94,0.4)':'rgba(220,38,38,0.3)'}`, borderRadius:100, padding:'0.4rem', cursor:'pointer' }}>
                            {p.hidden ? 'Show' : 'Hide'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Testimonials */}
            {tab==='testimonials' && <TestimonialsAdmin requestAction={requestAction}/>}

            {/* Visits */}
            {tab==='visits' && (() => {
              const max = Math.max(1, ...visits.map(v=>v.count||0))
              return (
                <div>
                  <div style={{ display:'flex', gap:'0.75rem', marginBottom:'1.5rem', flexWrap:'wrap' }}>
                    <div className="card" style={{ padding:'0.875rem 1.25rem' }}>
                      <p style={{ fontFamily:'Gibs, serif', fontSize:'1.3rem', color:'#1A1A1A', margin:0 }}>{stats?.visitsToday ?? 0}</p>
                      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#6B6B6B', margin:0, fontWeight:600 }}>Visits today</p>
                    </div>
                    <div className="card" style={{ padding:'0.875rem 1.25rem' }}>
                      <p style={{ fontFamily:'Gibs, serif', fontSize:'1.3rem', color:'#1A1A1A', margin:0 }}>{stats?.visits30 ?? 0}</p>
                      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#6B6B6B', margin:0, fontWeight:600 }}>Last 30 days</p>
                    </div>
                  </div>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#9B9B9B', margin:'0 0 0.75rem', fontWeight:600 }}>Daily visits — last 30 days</p>
                  {visits.length === 0 ? (
                    <p style={{ color:'#BCBCBC', textAlign:'center', padding:'2rem' }}>No visits recorded yet</p>
                  ) : (
                    <div className="card" style={{ padding:'1.25rem' }}>
                      <AxisBarChart data={visits.map(v=>({ label:fmtDate(v.day).replace(/ \d{4}$/,''), value:v.count||0 }))} color="#C4826A" height={260} yTitle="Visits" xTitle="Date (last 30 days)"/>
                    </div>
                  )}
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#BCBCBC', margin:'0.5rem 0 0' }}>Counted once per browser session. Hover a bar for the date &amp; count.</p>
                </div>
              )
            })()}

            {/* Agents */}
            {tab==='agents' && (() => {
              async function addAgent(e) {
                e.preventDefault()
                const email = agentEmail.trim().toLowerCase()
                const name  = agentName.trim()
                if (!email) return
                setAgentSaving(true)
                const { data: { session } } = await supabase.auth.getSession()
                await fetch(
                  import.meta.env.VITE_SUPABASE_URL + '/functions/v1/cs-agents',
                  {
                    method:  'POST',
                    headers: { 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
                    body:    JSON.stringify({ action: 'add', email, name }),
                  }
                )
                setAgentEmail('')
                setAgentName('')
                setAgentSaving(false)
                fetchAgents()
              }
              async function removeAgent(email) {
                const { data: { session } } = await supabase.auth.getSession()
                await fetch(
                  import.meta.env.VITE_SUPABASE_URL + '/functions/v1/cs-agents',
                  {
                    method:  'POST',
                    headers: { 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
                    body:    JSON.stringify({ action: 'remove', email }),
                  }
                )
                fetchAgents()
              }
              return (
                <div>
                  <p style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.78rem', color:'#9B9B9B', margin:'0 0 1.25rem' }}>
                    These emails can access the <a href="/support" target="_blank" style={{ color:'#C4826A' }}>/support</a> dashboard to handle live customer chats.
                  </p>

                  {/* Add agent form */}
                  <form onSubmit={addAgent} style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap', marginBottom:'1.25rem' }}>
                    <input
                      value={agentEmail}
                      onChange={e => setAgentEmail(e.target.value)}
                      placeholder="agent@example.com"
                      type="email"
                      required
                      style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.85rem', border:'1px solid #E5E0D8', borderRadius:100, padding:'0.6rem 1rem', outline:'none', flex:'1 1 200px' }}
                    />
                    <input
                      value={agentName}
                      onChange={e => setAgentName(e.target.value)}
                      placeholder="Name (optional)"
                      style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.85rem', border:'1px solid #E5E0D8', borderRadius:100, padding:'0.6rem 1rem', outline:'none', flex:'1 1 140px' }}
                    />
                    <button type="submit" disabled={agentSaving} style={{ fontFamily:'Nunito Sans,sans-serif', fontWeight:700, fontSize:'0.82rem', color:'white', background:agentSaving?'#D4C4BC':'#C4826A', border:'none', borderRadius:100, padding:'0.6rem 1.25rem', cursor:agentSaving?'default':'pointer', display:'flex', alignItems:'center', gap:'0.4rem', flexShrink:0 }}>
                      <Plus size={14}/> Add Agent
                    </button>
                  </form>

                  {/* Agent list */}
                  {agents.length === 0 && (
                    <p style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.82rem', color:'#BCBCBC', textAlign:'center', padding:'1.5rem' }}>
                      No agents yet — add one above
                    </p>
                  )}
                  <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                    {agents.map(a => (
                      <div key={a.id} className="card" style={{ padding:'0.75rem 1rem', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.75rem' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'0.625rem', flex:1, minWidth:0 }}>
                          <Headphones size={16} color='#C4826A' style={{ flexShrink:0 }}/>
                          <div style={{ minWidth:0 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap' }}>
                              {a.name && <p style={{ fontFamily:'Nunito Sans,sans-serif', fontWeight:700, fontSize:'0.85rem', color:'#1A1A1A', margin:0 }}>{a.name}</p>}
                              {a.resolvedCount > 0 && (
                                <span style={{ background:'#9BAF9B', color:'white', borderRadius:100, fontSize:'0.62rem', fontWeight:700, padding:'2px 8px', lineHeight:'1.4', flexShrink:0 }}>
                                  {a.resolvedCount} resolved
                                </span>
                              )}
                            </div>
                            <p style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.78rem', color:'#6B6B6B', margin:0 }}>{a.email}</p>
                          </div>
                        </div>
                        <button onClick={() => removeAgent(a.email)} title="Remove agent" style={{ background:'none', border:'none', cursor:'pointer', padding:4, color:'#BCBCBC' }}>
                          <Trash2 size={15}/>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}

            {/* Discovery */}
            {tab==='discovery' && (() => {
              const labels = { instagram:'Instagram', tiktok:'TikTok', youtube:'YouTube', friend:'Friend told me', google:'Google', vendor:'Wedding vendor', church:'Church/community', other:'Other' }
              const total  = Object.values(discoveryCounts).reduce((s,n)=>s+n,0)
              const rows   = Object.entries(discoveryCounts).sort((a,b)=>b[1]-a[1])
              return (
                <div>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#9B9B9B', margin:'0 0 1rem' }}>
                    {total} couple{total!==1?'s':''} answered · {Math.max(0,(stats?.weddings||0) - total)} skipped or not yet asked
                  </p>
                  {rows.length === 0 && <p style={{ color:'#BCBCBC', textAlign:'center', padding:'2rem' }}>No data yet</p>}
                  <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                    {rows.map(([key, count])=>{
                      const pct = total > 0 ? Math.round((count/total)*100) : 0
                      return (
                        <div key={key} className="card" style={{ padding:'0.75rem 1rem' }}>
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.35rem' }}>
                            <span style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', color:'#1A1A1A' }}>{labels[key]||key}</span>
                            <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#C4826A', fontWeight:700 }}>{count} <span style={{ color:'#BCBCBC', fontWeight:400 }}>({pct}%)</span></span>
                          </div>
                          <div style={{ height:6, background:'#F3F0EA', borderRadius:100, overflow:'hidden' }}>
                            <div style={{ height:'100%', width:`${pct}%`, background:'#C4826A', borderRadius:100, transition:'width 0.4s' }}/>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })()}
          </>
        )}
        </div>
      </main>

      {/* Re-auth confirmation modal */}
        {reauth && (
          <div onClick={()=>!reauthBusy && setReauth(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:'1rem' }}>
            <div onClick={e=>e.stopPropagation()} style={{ background:'white', borderRadius:'1.25rem', padding:'1.75rem', width:'100%', maxWidth:380, boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>
              <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.2rem', margin:'0 0 0.5rem' }}>Confirm it's you</h3>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#1A1A1A', margin:'0 0 0.25rem', lineHeight:1.6 }}>{reauth.label}</p>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#9B9B9B', margin:'0 0 1rem' }}>Re-enter your admin password{my2fa ? ' and your 2FA code' : ''} to continue.</p>
              <input type="password" value={reauthPw} onChange={e=>setReauthPw(e.target.value)} autoFocus placeholder="Your password"
                onKeyDown={e=>{ if(e.key==='Enter' && !my2fa) confirmReauth() }}
                style={{ width:'100%', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.9rem', border:`1.5px solid ${reauthErr?'#DC2626':'#E5E0D8'}`, borderRadius:'0.75rem', padding:'0.7rem 1rem', outline:'none', boxSizing:'border-box', marginBottom:'0.5rem' }}/>
              {my2fa && (
                <input value={reauthCode} onChange={e=>setReauthCode(e.target.value.replace(/\D/g,'').slice(0,6))} placeholder="6-digit code" inputMode="numeric"
                  onKeyDown={e=>{ if(e.key==='Enter') confirmReauth() }}
                  style={{ width:'100%', fontFamily:'monospace', letterSpacing:'0.3em', textAlign:'center', fontSize:'1rem', border:`1.5px solid ${reauthErr?'#DC2626':'#E5E0D8'}`, borderRadius:'0.75rem', padding:'0.7rem 1rem', outline:'none', boxSizing:'border-box', marginBottom:'0.5rem' }}/>
              )}
              {reauthErr && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#DC2626', margin:'0 0 0.75rem' }}>{reauthErr}</p>}
              <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.5rem' }}>
                <button onClick={()=>setReauth(null)} disabled={reauthBusy} style={{ flex:1, fontFamily:'Nunito Sans, sans-serif', fontWeight:600, fontSize:'0.82rem', color:'#6B6B6B', background:'#F3F0EA', border:'none', borderRadius:100, padding:'0.7rem', cursor:'pointer' }}>Cancel</button>
                <button onClick={confirmReauth} disabled={reauthBusy} style={{ flex:1, fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem', color:'white', background:reauthBusy?'#D4C4BC':'#C4826A', border:'none', borderRadius:100, padding:'0.7rem', cursor:reauthBusy?'default':'pointer' }}>{reauthBusy?'Verifying…':'Confirm'}</button>
              </div>
            </div>
          </div>
        )}
    </div>
  )
}
